import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./SuppliersPage.css";
import "./OwnWarehousePage.css";
import { SpCheck, SpInput, SpReadField, SpSelect } from "../components/SpPrimitives";
import FulfillmentPage from "./FulfillmentPage";
import OwnWarehouseReturnsTab from "./OwnWarehouseReturnsTab";
import OwnWarehouseFulfillmentIssuesTab from "./OwnWarehouseFulfillmentIssuesTab";
import OwnWarehouseWriteoffsTab from "./OwnWarehouseWriteoffsTab";
import OWN_WAREHOUSE_HELP from "./ownWarehouseHelp";
import { downloadBusinessStockReport } from "../api/businessStockApi";
import { getFulfillmentStockSnapshotSummary } from "../api/fulfillmentApi";
import {
    applyOwnWarehouseRepricing,
    checkOwnWarehouseNpRegistryStatus,
    closeOwnWarehouseNpRegistry,
    openOwnWarehouseNpRegistryPdf,
    createOwnWarehouse,
    createOwnWarehouseSenderProfile,
    createOwnWarehouseAdjustment,
    downloadOwnWarehouseStock,
    dryRunOwnWarehouseOffers,
    getOwnWarehouseDailyMetrics,
    getOwnWarehouseExpiring,
    getOwnWarehouseLedger,
    getOwnWarehousePrerequisites,
    getOwnWarehouseSenderProfilesReadiness,
    getOwnWarehouseSettings,
    getOwnWarehouseNpRegistries,
    getOwnWarehouseSkuBatches,
    getOwnWarehouseStock,
    getOwnWarehouseTurnover,
    getOwnWarehouseValuation,
    listOwnWarehouseRepricingRuns,
    listOwnWarehouseReservations,
    listOwnWarehouses,
    previewOwnWarehouseRepricing,
    recomputeOwnWarehouseMetrics,
    refreshOwnWarehouseOffers,
    updateOwnWarehouse,
    updateOwnWarehouseSenderProfile,
    updateOwnWarehouseSettings,
} from "../api/ownWarehouseApi";

/* ============================================================
   Раздел «Склад» — собственные склады.
   Склад = отправитель (supplier D19, D20…) = юрлицо. Партии FIFO,
   резерв под заказ, списание по подтверждённому составу и передаче НП, базовая розничная
   цена, переоценка, динамика. План: docs/own_warehouse_development_plan.md.
   ============================================================ */

const TABS = [
    { key: "overview", label: "Обзор" },
    { key: "issues", label: "Требуют проверки" },
    { key: "registry", label: "Собственные склады" },
    { key: "np-registries", label: "Реестры НП" },
    { key: "stock", label: "Остатки и партии" },
    { key: "movements", label: "Движения" },
    { key: "returns", label: "Возвраты" },
    { key: "writeoffs", label: "Списания" },
    { key: "invoices", label: "Накладные" },
    { key: "pricing", label: "Цены / переоценка" },
    { key: "dynamics", label: "Динамика" },
    { key: "external-stocks", label: "Внешние остатки" },
    { key: "settings", label: "Настройки" },
];

const ROUNDING_OPTIONS = [
    { value: "none", label: "Без округления" },
    { value: "integer", label: "До целой гривны" },
    { value: "half", label: "До 50 копеек" },
];

const ADJUSTMENT_REASONS = [
    { value: "inventory_count", label: "Инвентаризация" },
    { value: "breakage", label: "Бой" },
    { value: "defect", label: "Брак" },
    { value: "expired", label: "Истёк срок" },
    { value: "other", label: "Другое" },
];

const EVENT_TYPES = [
    { value: "invoice_posted", label: "Проведение накладной" },
    { value: "invoice_unposted", label: "Отмена проведения" },
    { value: "manual_adjustment", label: "Корректировка ±" },
    { value: "return_received", label: "Возврат от покупателя" },
    { value: "order_reserved", label: "Резерв под заказ" },
    { value: "reservation_released", label: "Снятие резерва" },
    { value: "order_written_off", label: "Списание по заказу" },
    { value: "writeoff_posted", label: "Списание по документу" },
    { value: "writeoff_cancelled", label: "Отмена списания" },
];
const eventLabel = (value) => EVENT_TYPES.find((item) => item.value === value)?.label || value || "—";

const RESERVATION_STATUSES = [
    { value: "active", label: "Активные" },
    { value: "released", label: "Снятые" },
    { value: "written_off", label: "Списанные" },
    { value: "expired", label: "Истёкшие" },
];

const REPRICING_MODES = [
    { value: "markup_fifo", label: "% к себестоимости FIFO (текущая партия)" },
    { value: "markup_avg", label: "% к средней себестоимости" },
    { value: "markup_last", label: "% к себестоимости последнего прихода" },
    { value: "manual", label: "Цена вручную по SKU" },
];

const PRICE_SOURCE_LABELS = {
    invoice_markup: "накладная, %",
    invoice_manual: "накладная, вручную",
    repricing_markup: "переоценка, %",
    repricing_manual: "переоценка, вручную",
    manual: "вручную",
};
const priceSourceLabel = (value) => PRICE_SOURCE_LABELS[value] || value || "—";

const fmtNum = (value, digits = 2) => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    return number.toLocaleString("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits });
};
const fmtQty = (value) => fmtNum(value, 0);
const fmtDate = (value) => (value ? String(value).slice(0, 10) : "—");
const fmtDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("ru-RU");
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().slice(0, 10);
};
const apiError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (Array.isArray(detail) && detail.length) return detail.map((item) => item?.msg || JSON.stringify(item)).join("; ");
    return err?.message || fallback;
};
const toNumberOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const splitIds = (value) => String(value || "")
    .split(/[,\s;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => Number(item))
    .filter((item) => Number.isInteger(item));

const Msg = ({ error, message }) => (
    <>
        {error ? <div className="sp-msg err">{error}</div> : null}
        {message ? <div className="sp-msg ok">{message}</div> : null}
    </>
);

const EmptyWarehouse = () => (
    <div className="sp-card sp-panel">
        <div className="sp-callout">
            <span className="ic">i</span>
            <div>
                <b>Склад не выбран.</b> Создайте первый собственный склад во вкладке «Склады» (мастер создания) или выберите существующий в шапке раздела.
            </div>
        </div>
    </div>
);

const WarehouseOverviewTab = ({ warehouses, contourEnabled, onNavigate, onSelectWarehouse }) => {
    const [external, setExternal] = useState(null);
    const [externalLoading, setExternalLoading] = useState(true);
    const [externalError, setExternalError] = useState("");

    const loadExternalSummary = useCallback(async () => {
        setExternalLoading(true);
        setExternalError("");
        try {
            setExternal(await getFulfillmentStockSnapshotSummary());
        } catch (err) {
            setExternal(null);
            setExternalError("Сводка внешних остатков сейчас недоступна.");
        } finally {
            setExternalLoading(false);
        }
    }, []);

    useEffect(() => { loadExternalSummary(); }, [loadExternalSummary]);

    const ownTotals = useMemo(() => warehouses.reduce((totals, warehouse) => {
        const stats = warehouse.stats || {};
        totals.skuInStock += Number(stats.sku_in_stock) || 0;
        totals.onHand += Number(stats.on_hand_qty) || 0;
        totals.reserved += Number(stats.reserved_qty) || 0;
        totals.value += Number(stats.stock_value) || 0;
        return totals;
    }, { skuInStock: 0, onHand: 0, reserved: 0, value: 0 }), [warehouses]);

    const openOwnWarehouse = (tab, warehouseId) => {
        if (warehouseId) onSelectWarehouse(warehouseId);
        const query = warehouseId ? `?wh=${encodeURIComponent(warehouseId)}` : "";
        onNavigate(`/warehouse/${tab}${query}`);
    };

    return (
        <div className="ow-dashboard">
            <section className="sp-card ow-dashboard-own" aria-labelledby="ow-dashboard-own-title">
                <div className="ow-dashboard-heading">
                    <div>
                        <h3 id="ow-dashboard-own-title">Собственные склады</h3>
                        <p>Остаток, доступность, резервы и себестоимость по складам.</p>
                    </div>
                    <span className={`ow-dashboard-status ${contourEnabled === true ? "is-on" : contourEnabled === false ? "is-off" : "is-unknown"}`}>
                        {contourEnabled === true ? "Контур включён" : contourEnabled === false ? "Контур выключен" : "Статус не загружен"}
                    </span>
                </div>
                {warehouses.length ? (
                    <>
                        <div className="ow-dashboard-kpis">
                            <div><span>Складов</span><b>{warehouses.length}</b></div>
                            <div><span>SKU в наличии</span><b>{fmtQty(ownTotals.skuInStock)}</b></div>
                            <div><span>Доступно, шт.</span><b>{fmtQty(ownTotals.onHand - ownTotals.reserved)}</b></div>
                            <div><span>В резерве, шт.</span><b>{fmtQty(ownTotals.reserved)}</b></div>
                            <div><span>Себестоимость</span><b>{fmtNum(ownTotals.value)} ₴</b></div>
                        </div>
                        <div className="ow-dashboard-warehouse-list">
                            {warehouses.map((warehouse) => {
                                const stats = warehouse.stats || {};
                                return (
                                    <button
                                        type="button"
                                        className="ow-dashboard-warehouse"
                                        key={warehouse.id}
                                        onClick={() => openOwnWarehouse("stock", warehouse.id)}
                                        aria-label={`Открыть остатки ${warehouse.code} ${warehouse.name}`}
                                    >
                                        <span className="ow-dashboard-warehouse-name"><b>{warehouse.code} · {warehouse.name}</b><small>{warehouse.business_organization_name || "Юрлицо не указано"}</small></span>
                                        <span><small>SKU</small><b>{fmtQty(stats.sku_in_stock)}</b></span>
                                        <span><small>Доступно</small><b>{fmtQty((Number(stats.on_hand_qty) || 0) - (Number(stats.reserved_qty) || 0))}</b></span>
                                        <span><small>Резерв</small><b>{fmtQty(stats.reserved_qty)}</b></span>
                                        <span className="ow-dashboard-row-value"><small>Себестоимость</small><b>{fmtNum(stats.stock_value)} ₴</b></span>
                                    </button>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <div className="ow-dashboard-empty">
                        <p>Собственных складов пока нет. Создайте первый склад, чтобы вести партии, резервы и себестоимость.</p>
                        <button type="button" className="sp-btn sp-btn-primary sp-btn-sm" onClick={() => onNavigate("/warehouse/registry")}>Создать склад</button>
                    </div>
                )}
                <div className="ow-dashboard-actions">
                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => openOwnWarehouse("registry", warehouses[0]?.id)}>Собственные склады</button>
                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => openOwnWarehouse("stock", warehouses[0]?.id)}>Остатки и партии</button>
                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => openOwnWarehouse("invoices", warehouses[0]?.id)}>Накладные</button>
                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => openOwnWarehouse("pricing", warehouses[0]?.id)}>Цены / переоценка</button>
                </div>
            </section>

            <section className="sp-card ow-dashboard-external" aria-labelledby="ow-dashboard-external-title">
                <div className="ow-dashboard-heading">
                    <div>
                        <h3 id="ow-dashboard-external-title">Внешние остатки · fulfillment</h3>
                        <p>Остатки внешних складов и Новой Почты — отдельный справочный контур.</p>
                    </div>
                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={loadExternalSummary} disabled={externalLoading}>
                        {externalLoading ? "Обновляю…" : "Обновить"}
                    </button>
                </div>
                {externalLoading ? <p className="sp-hint">Загружаю сводку…</p> : externalError ? (
                    <div className="ow-dashboard-degraded"><span>{externalError}</span><button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={loadExternalSummary}>Повторить</button></div>
                ) : (
                    <div className="ow-dashboard-kpis ow-dashboard-external-kpis">
                        <div><span>Складов</span><b>{fmtQty(external?.warehouse_count)}</b></div>
                        <div><span>SKU</span><b>{fmtQty(external?.sku_count)}</b></div>
                        <div><span>Доступно, шт.</span><b>{fmtQty(external?.available_qty)}</b></div>
                        <div><span>Карантин / брак</span><b>{fmtQty(external?.quarantine_qty)} / {fmtQty(external?.defect_qty)}</b></div>
                        <div><span>Проблемных строк</span><b>{fmtQty(external?.problem_rows)}</b></div>
                        <div><span>Последнее обновление</span><b>{external?.last_fact_at || external?.updated_at ? fmtDateTime(external.last_fact_at || external.updated_at) : "Нет данных"}</b></div>
                    </div>
                )}
                <div className="ow-dashboard-actions">
                    <button type="button" className="sp-btn sp-btn-primary sp-btn-sm" onClick={() => onNavigate("/warehouse/external-stocks")}>Открыть внешние остатки</button>
                </div>
            </section>
        </div>
    );
};

const WarehouseExternalStocksTab = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const handleDownload = async () => {
        setError("");
        setLoading(true);
        try {
            await downloadBusinessStockReport();
        } catch (err) {
            setError("Не удалось сформировать отчёт. Попробуйте ещё раз.");
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="ow-external-stocks">
            <div className="sp-card ow-external-stocks-heading">
                <div><h3>Внешние остатки</h3><p>Снимки внешнего fulfillment. Для собственных партий и резервов перейдите во вкладку «Остатки и партии».</p></div>
                <button type="button" className="sp-btn sp-btn-primary" onClick={handleDownload} disabled={loading}>{loading ? "Формирование отчёта…" : "Сформировать Excel"}</button>
            </div>
            {loading ? <p className="sp-hint">Идёт сбор Excel по текущему snapshot остатков, это может занять до пары минут.</p> : null}
            {error ? <div className="sp-msg err">{error}</div> : null}
            <FulfillmentPage embedded mode="stocks" />
        </div>
    );
};

/* ------------------------------------------------------------------
   SVG-графики без зависимостей (одна ось на график; подсказки через <title>)
   ------------------------------------------------------------------ */
const CHART_W = 640;
const CHART_H = 180;
const PAD = { l: 48, r: 12, t: 12, b: 26 };

const niceMax = (value) => {
    if (!value || value <= 0) return 1;
    const power = 10 ** Math.floor(Math.log10(value));
    const unit = value / power;
    const step = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10;
    return step * power;
};
const tickLabel = (value) => (Math.abs(value) >= 1000 ? `${(value / 1000).toLocaleString("ru-RU", { maximumFractionDigits: 1 })}k` : value.toLocaleString("ru-RU", { maximumFractionDigits: 0 }));

const LineChart = ({ title, subtitle, labels, values, color, unit = "" }) => {
    const nums = values.map((value) => Number(value) || 0);
    const max = niceMax(Math.max(...nums, 0));
    const innerW = CHART_W - PAD.l - PAD.r;
    const innerH = CHART_H - PAD.t - PAD.b;
    const stepX = nums.length > 1 ? innerW / (nums.length - 1) : 0;
    const points = nums.map((value, index) => ({
        x: PAD.l + index * stepX,
        y: PAD.t + innerH - (value / max) * innerH,
        value,
        label: labels[index],
    }));
    const path = points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
    const ticks = [0, 0.5, 1].map((fraction) => ({ fraction, value: max * fraction }));
    const labelEvery = Math.max(1, Math.ceil(nums.length / 6));
    return (
        <div className="ow-chart">
            <div className="t">{title}</div>
            {subtitle ? <div className="s">{subtitle}</div> : null}
            {nums.length === 0 ? <div className="sp-hint">Нет данных за период</div> : (
                <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} role="img" aria-label={title}>
                    {ticks.map((tick) => {
                        const y = PAD.t + innerH - tick.fraction * innerH;
                        return (
                            <g key={tick.fraction}>
                                <line className="grid" x1={PAD.l} x2={CHART_W - PAD.r} y1={y} y2={y} />
                                <text className="axis" x={PAD.l - 6} y={y + 4} textAnchor="end">{tickLabel(tick.value)}</text>
                            </g>
                        );
                    })}
                    {points.map((point, index) => (index % labelEvery === 0 ? (
                        <text key={`x-${point.label}`} className="axis" x={point.x} y={CHART_H - 8} textAnchor="middle">{String(point.label).slice(5)}</text>
                    ) : null))}
                    <path className="line" d={path} stroke={color} />
                    {points.map((point) => (
                        <circle key={point.label} className="dot" cx={point.x} cy={point.y} r={points.length > 60 ? 2.5 : 4} fill={color}>
                            <title>{`${point.label}: ${fmtNum(point.value, unit === "₴" ? 2 : 0)} ${unit}`.trim()}</title>
                        </circle>
                    ))}
                </svg>
            )}
        </div>
    );
};

const GroupedBarChart = ({ title, subtitle, labels, series }) => {
    const all = series.flatMap((item) => item.values.map((value) => Number(value) || 0));
    const max = niceMax(Math.max(...all, 0));
    const innerW = CHART_W - PAD.l - PAD.r;
    const innerH = CHART_H - PAD.t - PAD.b;
    const groups = labels.length || 1;
    const groupW = innerW / groups;
    const barW = Math.max(2, (groupW - 4) / Math.max(series.length, 1) - 1);
    const ticks = [0, 0.5, 1].map((fraction) => ({ fraction, value: max * fraction }));
    const labelEvery = Math.max(1, Math.ceil(labels.length / 6));
    return (
        <div className="ow-chart">
            <div className="t">{title}</div>
            {subtitle ? <div className="s">{subtitle}</div> : null}
            <div className="ow-legend">
                {series.map((item) => <span key={item.key}><span className="sw" style={{ background: item.color }} />{item.label}</span>)}
            </div>
            {labels.length === 0 ? <div className="sp-hint">Нет данных за период</div> : (
                <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} role="img" aria-label={title}>
                    {ticks.map((tick) => {
                        const y = PAD.t + innerH - tick.fraction * innerH;
                        return (
                            <g key={tick.fraction}>
                                <line className="grid" x1={PAD.l} x2={CHART_W - PAD.r} y1={y} y2={y} />
                                <text className="axis" x={PAD.l - 6} y={y + 4} textAnchor="end">{tickLabel(tick.value)}</text>
                            </g>
                        );
                    })}
                    {labels.map((label, groupIndex) => {
                        const x0 = PAD.l + groupIndex * groupW + 2;
                        return (
                            <g key={label}>
                                {groupIndex % labelEvery === 0 ? (
                                    <text className="axis" x={x0 + (groupW - 4) / 2} y={CHART_H - 8} textAnchor="middle">{String(label).slice(5)}</text>
                                ) : null}
                                {series.map((item, seriesIndex) => {
                                    const value = Number(item.values[groupIndex]) || 0;
                                    const height = (value / max) * innerH;
                                    const x = x0 + seriesIndex * (barW + 1);
                                    return (
                                        <rect
                                            key={item.key}
                                            className="bar"
                                            x={x}
                                            y={PAD.t + innerH - height}
                                            width={barW}
                                            height={height}
                                            rx={Math.min(2, barW / 2)}
                                            fill={item.color}
                                        >
                                            <title>{`${label} · ${item.label}: ${fmtQty(value)}`}</title>
                                        </rect>
                                    );
                                })}
                            </g>
                        );
                    })}
                </svg>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------
   Вкладка «Склады»: реестр + карточка (Профиль / Создание)
   ------------------------------------------------------------------ */
const profileDraftFrom = (warehouse) => ({
    name: warehouse?.name || "",
    is_active: Boolean(warehouse?.is_active),
    stock_enabled: Boolean(warehouse?.stock_enabled),
    orders_enabled: Boolean(warehouse?.orders_enabled),
    np_registry_enabled: Boolean(warehouse?.np_registry_enabled),
    default_markup_percent: warehouse?.default_markup_percent ?? "",
    price_rounding_mode: warehouse?.price_rounding_mode || "none",
    emit_zero_stock_offers: Boolean(warehouse?.emit_zero_stock_offers),
    nova_poshta_sender_point_id: warehouse?.nova_poshta_sender_point_id ?? "",
    address_text: warehouse?.address_text || "",
    notes: warehouse?.notes || "",
    sender_priority: warehouse?.sender?.priority ?? "",
    sender_publish_priority_tier: warehouse?.sender?.publish_priority_tier ?? "",
    sender_salesdrive_supplier_id: warehouse?.sender?.salesdrive_supplier_id ?? "",
    sender_city: warehouse?.sender?.city || "",
    sender_is_active: warehouse?.sender ? Boolean(warehouse.sender.is_active) : true,
});

const NpRegistriesTab = ({ warehouse, reloadWarehouses }) => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const load = useCallback(async () => {
        if (!warehouse?.id) { setData(null); return; }
        setBusy(true); setError("");
        try { setData(await getOwnWarehouseNpRegistries(warehouse.id)); }
        catch (err) { setError(apiError(err, "Не удалось загрузить реестры Новой почты")); }
        finally { setBusy(false); }
    }, [warehouse?.id]);
    useEffect(() => { load(); }, [load]);

    const toggleEnabled = async (enabled) => {
        setBusy(true); setError(""); setMessage("");
        try {
            await updateOwnWarehouse(warehouse.id, { np_registry_enabled: enabled });
            setMessage(enabled ? "Реестры Новой почты включены для склада" : "Приём новых заказов в реестры выключен");
            await Promise.all([load(), reloadWarehouses()]);
        } catch (err) { setError(apiError(err, "Не удалось изменить настройку")); }
        finally { setBusy(false); }
    };
    const checkStatus = async () => {
        setChecking(true); setError(""); setMessage("");
        try {
            const result = await checkOwnWarehouseNpRegistryStatus(warehouse.id);
            setMessage(result.np_status_changed ? "Новая почта уже изменила статус одной или нескольких ЭН. Проверьте реестр и закройте его после передачи." : "Статусы ЭН проверены: все в начальном состоянии.");
            await load();
        } catch (err) { setError(apiError(err, "Не удалось проверить статусы Новой почты")); }
        finally { setChecking(false); }
    };
    const close = async (register) => {
        if (!window.confirm(`Закрыть реестр ${register.np_number || register.np_ref || `#${register.id}`} вручную? Обычно он закрывается сам, когда Новая почта примет все его посылки.`)) return;
        setBusy(true); setError(""); setMessage("");
        try {
            await closeOwnWarehouseNpRegistry(warehouse.id, register.id);
            setMessage("Реестр закрыт в нашей системе и сохранён в истории");
            await load();
        } catch (err) { setError(apiError(err, "Не удалось закрыть реестр")); }
        finally { setBusy(false); }
    };
    // Реестр НП принадлежит одному отправителю: при отправке от разных юрлиц открыто по реестру на каждое.
    const openRegistries = data?.open_registries || (data?.open ? [data.open] : []);
    const queuedRows = data?.queued_rows || [];
    const npCell = (row) => {
        if (!row.np_status_code) return "—";
        const label = row.np_class === "accepted" ? "Принято НП" : row.np_class === "deleted" ? "ЭН удалена" : row.np_class === "created" ? "Ждёт курьера" : `код ${row.np_status_code}`;
        return <span title={row.np_status || ""}>{label}{row.np_status_at ? ` · ${fmtDateTime(row.np_status_at)}` : ""}</span>;
    };
    const renderRows = (list) => (
        <div className="sp-table-wrap"><table className="sp-table"><thead><tr><th>ТТН</th><th>Заказ SalesDrive</th><th>Заказ</th><th>Статус 4</th><th>Добавление</th><th>НП</th><th>Ошибка</th></tr></thead>
            <tbody>{list.length ? list.map((row) => <tr key={row.id}><td>{row.ttn_number || "—"}</td><td>{row.salesdrive_order_id}</td><td>{row.order_number || row.tabletki_order_number || row.external_id || "—"}</td><td>{fmtDateTime(row.status4_at)}</td><td>{row.sync_state === "added" ? "Добавлена" : row.sync_state === "pending" ? "В очереди" : "Ошибка"}</td><td>{npCell(row)}</td><td>{row.last_error || "—"}</td></tr>) : <tr><td colSpan="7">ЭН пока нет</td></tr>}</tbody>
        </table></div>
    );
    const stateTitle = (register) => register.state === "handover" ? "Передаётся курьеру" : register.state === "opening" ? "Создаётся в НП" : "Открытый реестр";
    const closeReason = (register) => ({ manual: "закрыт вручную", auto_accepted: "закрыт автоматически: все ЭН приняты НП", auto_partial_timeout: "закрыт по таймауту: часть ЭН не передана" }[register.close_reason] || "закрыт");
    const openPdf = async (register) => {
        try { await openOwnWarehouseNpRegistryPdf(warehouse.id, register.id); }
        catch (err) { setError(apiError(err, "Не удалось получить форму реестра НП")); }
    };
    if (!warehouse) return <EmptyWarehouse />;
    return <div className="sp-card sp-panel">
        <div className="sp-panel-head"><div><h3>Реестры Новой почты · {warehouse.name}</h3><p>ЭН собственного склада, включая вручную созданные ТТН, автоматически попадают сюда после status 4.</p></div>
            <div className="sp-actions"><button className="sp-btn sp-btn-ghost sp-btn-sm" onClick={load} disabled={busy}>Обновить</button><button className="sp-btn sp-btn-ghost sp-btn-sm" onClick={checkStatus} disabled={checking || !openRegistries.length}>{checking ? "Проверяю…" : "Проверить статусы НП"}</button></div></div>
        <Msg error={error} message={message} />
        <div className="sp-callout"><div><b>Приём в реестры: {data?.enabled ? "включён" : "выключен"}.</b> Настройка действует только на выбранный собственный склад.</div><button className={data?.enabled ? "sp-btn sp-btn-ghost sp-btn-sm" : "sp-btn sp-btn-primary sp-btn-sm"} onClick={() => toggleEnabled(!data?.enabled)} disabled={busy}>{data?.enabled ? "Выключить" : "Включить для этого склада"}</button></div>
        <div className="sp-callout"><b>Реестры закрываются автоматически.</b> Как только курьер принял хотя бы одну посылку реестра, он переходит в «Передаётся курьеру» и новые ЭН идут в новый реестр; когда приняты все — реестр закрывается сам. Кнопка «Закрыть вручную» — запасной путь.</div>
        {openRegistries.length ? openRegistries.map((register) => <section className="sp-block" key={register.id}><div className="sp-bh"><span className="ic">{register.state === "handover" ? "🚚" : "📦"}</span><h4>{stateTitle(register)}{register.organization_name ? ` · ${register.organization_name}` : ""}</h4><span className="ow-registry-count">{register.accepted_count || 0} / {register.rows?.length ?? register.document_count} ЭН принято</span></div>
            <div className="sp-stats"><span className="sp-stat">Номер НП <b>{register.np_number || "ожидается"}</b></span><span className="sp-stat">Создан <b>{fmtDateTime(register.opened_at)}</b></span>{register.handover_at && <span className="sp-stat">Первая посылка принята <b>{fmtDateTime(register.handover_at)}</b></span>}<span className="sp-stat">Проверка НП <b>{register.np_checked_at ? fmtDateTime(register.np_checked_at) : "ещё не было"}</b></span><span className="sp-stat">Синхронизация <b>{register.last_error ? "есть ошибка" : "активна"}</b></span></div>
            {register.last_error && <div className="sp-msg err">{register.last_error}</div>}{renderRows(register.rows || [])}
            <div className="sp-actions">{register.np_ref && <button className="sp-btn sp-btn-ghost" onClick={() => openPdf(register)}>PDF реестра</button>}<button className="sp-btn sp-btn-ghost" onClick={() => close(register)} disabled={busy || !["open", "handover"].includes(register.state)}>Закрыть вручную</button></div>
        </section>) : <div className="sp-callout"><b>Открытого реестра нет.</b> Он будет создан после следующего подтверждённого перехода заказа 21 → 4.</div>}
        {(data?.left_behind_rows || []).length > 0 && <section className="sp-block"><div className="sp-bh"><span className="ic">⚠️</span><h4>Остались на складе</h4></div><div className="sp-msg err">Реестр закрыт по таймауту, а эти ЭН Новая почта так и не приняла. Их нужно отправить отдельно.</div>{renderRows(data.left_behind_rows)}</section>}
        {queuedRows.length > 0 && <section className="sp-block"><h4>Ожидают обработки</h4>{renderRows(queuedRows)}</section>}
        {(data?.failed || []).map((register) => <section className="sp-block" key={register.id}><div className="sp-msg err">Не удалось безопасно подтвердить реестр НП {register.np_number || register.np_ref || `#${register.id}`}: {register.last_error || "нужна сверка в кабинете Новой почты"}</div>{renderRows(register.rows || [])}</section>)}
        <section className="sp-block"><div className="sp-bh"><span className="ic">🗂</span><h4>История закрытых реестров</h4></div>
            {(data?.history || []).length ? data.history.map((register) => <details key={register.id} className="sp-card" style={{ marginBottom: 8 }}><summary>{register.np_number || register.np_ref || `Реестр #${register.id}`}{register.organization_name ? ` · ${register.organization_name}` : ""} · {fmtDateTime(register.closed_at)} · {register.accepted_count || 0}/{register.document_count} ЭН · {closeReason(register)}</summary>{renderRows(register.rows || [])}</details>) : <p>Закрытых реестров пока нет.</p>}
        </section>
    </div>;
};

const createDraftInitial = () => ({
    business_organization_id: "",
    code: "",
    name: "",
    salesdrive_supplier_id: "",
    nova_poshta_sender_point_id: "",
    default_markup_percent: "",
    price_rounding_mode: "none",
    city: "",
    store_mode: "all",
    store_ids: [],
    store_is_rrp: true,
    marketplace_account_ids: [],
    publish_priority_tier: "1",
    priority: "10",
    stock_enabled: true,
    orders_enabled: false,
    address_text: "",
    notes: "",
});

const RegistryTab = ({ warehouses, selected, onSelect, reload }) => {
    const [cardTab, setCardTab] = useState(selected ? "profile" : "create");
    const prevSelectedId = useRef(selected?.id || null);
    const [draft, setDraft] = useState(() => profileDraftFrom(selected));
    const [prereq, setPrereq] = useState(null);
    const [createDraft, setCreateDraft] = useState(createDraftInitial);
    const [createPrereq, setCreatePrereq] = useState(null);
    const [createPrereqState, setCreatePrereqState] = useState("idle"); // idle | loading | ready | error
    const [createPrereqAttempt, setCreatePrereqAttempt] = useState(0);
    const [senderProfileDraft, setSenderProfileDraft] = useState({ business_organization_id: "", nova_poshta_sender_point_id: "" });
    const [senderProfilePrereq, setSenderProfilePrereq] = useState(null);
    const [senderProfilesReady, setSenderProfilesReady] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    useEffect(() => {
        setDraft(profileDraftFrom(selected));
        // нет склада — только мастер; появился (авто-выбор ?wh= или клик) — открываем профиль
        const wasEmpty = !prevSelectedId.current;
        prevSelectedId.current = selected?.id || null;
        setCardTab((prev) => (!selected ? "create" : (prev === "create" && wasEmpty ? "profile" : prev)));
    }, [selected]);

    useEffect(() => {
        if (!selected?.id) { setSenderProfilesReady(null); return; }
        getOwnWarehouseSenderProfilesReadiness(selected.id).then(setSenderProfilesReady).catch(() => setSenderProfilesReady(null));
    }, [selected?.id, selected?.updated_at]);

    useEffect(() => {
        if (!senderProfileDraft.business_organization_id) { setSenderProfilePrereq(null); return; }
        let alive = true;
        getOwnWarehousePrerequisites(senderProfileDraft.business_organization_id)
            .then((data) => { if (alive) setSenderProfilePrereq(data); })
            .catch(() => { if (alive) setSenderProfilePrereq(null); });
        return () => { alive = false; };
    }, [senderProfileDraft.business_organization_id]);

    useEffect(() => {
        if (!selected?.business_organization_id) {
            setPrereq(null);
            return;
        }
        let alive = true;
        getOwnWarehousePrerequisites(selected.business_organization_id)
            .then((data) => { if (alive) setPrereq(data); })
            .catch(() => { if (alive) setPrereq(null); });
        return () => { alive = false; };
    }, [selected]);

    // мастер: без юрлица — только список юрлиц и следующий код; с юрлицом — полный чеклист
    useEffect(() => {
        if (cardTab !== "create") return;
        let alive = true;
        setCreatePrereqState("loading");
        getOwnWarehousePrerequisites(createDraft.business_organization_id || undefined)
            .then((data) => {
                if (!alive) return;
                setCreatePrereq(data);
                setCreatePrereqState("ready");
                setCreateDraft((prev) => ({
                    ...prev,
                    code: prev.code || data?.next_code || "",
                    nova_poshta_sender_point_id: prev.nova_poshta_sender_point_id
                        || String((data?.sender_points || []).find((point) => point.is_default)?.id || ""),
                }));
            })
            .catch(() => { if (alive) setCreatePrereqState("error"); });
        return () => { alive = false; };
    }, [cardTab, createDraft.business_organization_id, createPrereqAttempt]);

    const saveProfile = async () => {
        if (!selected) return;
        setBusy(true);
        setError("");
        setMessage("");
        try {
            await updateOwnWarehouse(selected.id, {
                name: draft.name,
                is_active: draft.is_active,
                stock_enabled: draft.stock_enabled,
                orders_enabled: draft.orders_enabled,
                np_registry_enabled: draft.np_registry_enabled,
                default_markup_percent: toNumberOrNull(draft.default_markup_percent),
                price_rounding_mode: draft.price_rounding_mode,
                emit_zero_stock_offers: draft.emit_zero_stock_offers,
                nova_poshta_sender_point_id: toNumberOrNull(draft.nova_poshta_sender_point_id),
                address_text: draft.address_text || null,
                notes: draft.notes || null,
                sender_priority: toNumberOrNull(draft.sender_priority),
                sender_publish_priority_tier: toNumberOrNull(draft.sender_publish_priority_tier),
                sender_salesdrive_supplier_id: toNumberOrNull(draft.sender_salesdrive_supplier_id),
                sender_city: draft.sender_city || null,
                sender_is_active: draft.sender_is_active,
            });
            setMessage("Склад сохранён");
            await reload();
        } catch (err) {
            setError(apiError(err, "Не удалось сохранить склад"));
        } finally {
            setBusy(false);
        }
    };

    const saveSenderProfile = async () => {
        if (!selected || !senderProfileDraft.business_organization_id || !senderProfileDraft.nova_poshta_sender_point_id) return;
        setBusy(true); setError(""); setMessage("");
        try {
            const existing = (selected.sender_profiles || []).find((item) => String(item.business_organization_id) === String(senderProfileDraft.business_organization_id));
            const payload = { nova_poshta_sender_point_id: Number(senderProfileDraft.nova_poshta_sender_point_id), is_active: true };
            if (existing) await updateOwnWarehouseSenderProfile(selected.id, existing.id, payload);
            else await createOwnWarehouseSenderProfile(selected.id, { ...payload, business_organization_id: Number(senderProfileDraft.business_organization_id) });
            setSenderProfileDraft({ business_organization_id: "", nova_poshta_sender_point_id: "" });
            setMessage("Профиль коммерческого отправителя сохранён");
            await reload();
        } catch (err) { setError(apiError(err, "Не удалось сохранить профиль отправителя")); }
        finally { setBusy(false); }
    };

    const toggleSenderProfile = async (profile) => {
        if (!selected) return;
        setBusy(true); setError(""); setMessage("");
        try {
            await updateOwnWarehouseSenderProfile(selected.id, profile.id, { is_active: !profile.is_active });
            setMessage(profile.is_active ? "Профиль отправителя выключен" : "Профиль отправителя включён");
            await reload();
        } catch (err) { setError(apiError(err, "Не удалось изменить статус профиля")); }
        finally { setBusy(false); }
    };

    const submitCreate = async () => {
        setBusy(true);
        setError("");
        setMessage("");
        try {
            const created = await createOwnWarehouse({
                code: createDraft.code.trim(),
                name: createDraft.name.trim(),
                business_organization_id: Number(createDraft.business_organization_id),
                salesdrive_supplier_id: toNumberOrNull(createDraft.salesdrive_supplier_id),
                nova_poshta_sender_point_id: toNumberOrNull(createDraft.nova_poshta_sender_point_id),
                default_markup_percent: toNumberOrNull(createDraft.default_markup_percent),
                price_rounding_mode: createDraft.price_rounding_mode,
                city: createDraft.city || null,
                store_ids: createDraft.store_mode === "all" ? null : createDraft.store_ids,
                store_is_rrp: createDraft.store_is_rrp,
                marketplace_account_ids: createDraft.marketplace_account_ids,
                publish_priority_tier: Number(createDraft.publish_priority_tier || 1),
                priority: Number(createDraft.priority || 10),
                stock_enabled: createDraft.stock_enabled,
                orders_enabled: createDraft.orders_enabled,
                address_text: createDraft.address_text || null,
                notes: createDraft.notes || null,
            });
            setMessage(`Склад ${created?.code || createDraft.code} создан`);
            setCreateDraft(createDraftInitial());
            await reload();
            if (created?.id) {
                onSelect(created.id);
                setCardTab("profile");
            }
        } catch (err) {
            setError(apiError(err, "Не удалось создать склад"));
        } finally {
            setBusy(false);
        }
    };

    const toggleId = (field, id) => setCreateDraft((prev) => {
        const set = new Set(prev[field]);
        if (set.has(id)) set.delete(id); else set.add(id);
        return { ...prev, [field]: Array.from(set) };
    });

    const senderPointOptions = (list) => (list || []).map((point) => ({
        value: String(point.id),
        label: `${point.point_name || `#${point.id}`}${point.point_type ? ` · ${point.point_type}` : ""}${point.is_default ? " · по умолчанию" : ""}${point.has_address === false ? " · без адреса" : ""}`,
    }));
    const legalEntityOptions = (createPrereq?.legal_entities || []).map((item) => ({ value: String(item.id), label: item.short_name || `#${item.id}` }));
    const senderProfileOrgOptions = (prereq?.legal_entities || createPrereq?.legal_entities || []).map((item) => ({ value: String(item.id), label: item.short_name || `#${item.id}` }));
    const orgChosen = Boolean(createDraft.business_organization_id);
    const checklist = orgChosen && createPrereq ? [
        { ok: Boolean(createPrereq.legal_entity_found), label: "Юрлицо найдено" },
        { ok: !createPrereq.legal_entity_occupied_by, label: createPrereq.legal_entity_occupied_by ? `Юрлицо уже занято складом ${createPrereq.legal_entity_occupied_by}` : "Юрлицо свободно" },
        { ok: Boolean(createPrereq.np_settings_found), label: "Настройки Новой Почты" },
        { ok: (createPrereq.sender_points || []).length > 0, label: `Точки отправки: ${(createPrereq.sender_points || []).length}` },
        { ok: Number(createPrereq.active_stores || 0) > 0, label: `Активных магазинов: ${createPrereq.active_stores ?? 0}` },
    ] : [];
    const canCreate = orgChosen && createDraft.code.trim() && createDraft.name.trim() && !createPrereq?.legal_entity_occupied_by && !busy;

    return (
        <div className="sp-section">
            <div className="sp-callout">
                <span className="ic">i</span>
                <div>
                    <b>Склад = отправитель = юрлицо.</b> Каждый собственный склад — это отправитель с кодом ряда D (D19, D20…) на своём юрлице:
                    остатки по партиям FIFO, резерв под заказ при приёме, списание по статусу доставки, базовая розничная цена и приоритет публикации.
                </div>
            </div>
            <Msg error={error} message={message} />
            <div className="sp-tbl-wrap">
                <table className="sp-tbl">
                    <thead>
                        <tr>
                            <th>Код</th><th>Название</th><th>Юрлицо</th><th>Остатки</th><th>Заказы</th><th>SKU в наличии</th><th>Остаток / резерв</th><th>Стоимость</th><th className="sp-go" />
                        </tr>
                    </thead>
                    <tbody>
                        {warehouses.map((item) => (
                            <tr
                                key={item.id}
                                className={String(item.id) === String(selected?.id) ? "sp-click sel" : "sp-click"}
                                tabIndex={0}
                                onClick={() => { onSelect(item.id); setCardTab("profile"); }}
                                onKeyDown={(event) => { if (event.key === "Enter") { onSelect(item.id); setCardTab("profile"); } }}
                            >
                                <td className="sp-strong">{item.code}</td>
                                <td>{item.name}{!item.is_active && <span className="sp-pill off" style={{ marginLeft: 6 }}>неактивен</span>}</td>
                                <td>{item.business_organization_name || `#${item.business_organization_id}`}</td>
                                <td><span className={item.stock_enabled ? "sp-pill g" : "sp-pill off"}>{item.stock_enabled ? "вкл" : "выкл"}</span></td>
                                <td><span className={item.orders_enabled ? "sp-pill g" : "sp-pill off"}>{item.orders_enabled ? "вкл" : "выкл"}</span></td>
                                <td>{fmtQty(item.stats?.sku_in_stock)} / {fmtQty(item.stats?.sku_total)}</td>
                                <td>{fmtQty(item.stats?.on_hand_qty)} / {fmtQty(item.stats?.reserved_qty)}</td>
                                <td>{fmtNum(item.stats?.stock_value)} ₴</td>
                                <td className="sp-go">›</td>
                            </tr>
                        ))}
                        {warehouses.length === 0 && <tr><td className="sp-mini" colSpan={9}>Собственных складов пока нет — создайте первый в карточке ниже</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="sp-card">
                <div className="sp-panel" style={{ paddingBottom: 0 }}>
                    <div className="sp-tabbar" style={{ marginBottom: 18 }}>
                        <button type="button" className={cardTab === "profile" ? "sp-tab active" : "sp-tab"} disabled={!selected} onClick={() => setCardTab("profile")}>Профиль{selected ? ` · ${selected.code}` : ""}</button>
                        <button type="button" className={cardTab === "create" ? "sp-tab active" : "sp-tab"} onClick={() => setCardTab("create")}>Создание</button>
                    </div>
                </div>
                {cardTab === "profile" && selected && (
                    <div className="sp-panel" style={{ paddingTop: 0 }}>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">🏬</span><h4>Склад</h4><span className="hint">id {selected.id} · код {selected.code}</span></div>
                            <div className="sp-grid3">
                                <SpInput label="Название" value={draft.name} onChange={(value) => setDraft({ ...draft, name: value })} />
                                <SpReadField label="Юрлицо" value={selected.business_organization_name || `#${selected.business_organization_id}`} />
                                <SpSelect
                                    label="Точка отправки Новой Почты"
                                    value={String(draft.nova_poshta_sender_point_id || "")}
                                    onChange={(value) => setDraft({ ...draft, nova_poshta_sender_point_id: value })}
                                    options={senderPointOptions(prereq?.sender_points)}
                                    placeholder={selected.sender_point_name ? `Текущая: ${selected.sender_point_name}` : "Не выбрана"}
                                />
                                <SpCheck label="Склад активен" checked={draft.is_active} onChange={(value) => setDraft({ ...draft, is_active: value })} />
                                <SpCheck label="Остатки публикуются (офферы)" checked={draft.stock_enabled} onChange={(value) => setDraft({ ...draft, stock_enabled: value })} />
                                <SpCheck label="Участвует в заказах (резерв/списание)" checked={draft.orders_enabled} onChange={(value) => setDraft({ ...draft, orders_enabled: value })} />
                                <SpCheck label="Реестры Новой почты по status 4" checked={draft.np_registry_enabled} onChange={(value) => setDraft({ ...draft, np_registry_enabled: value })} />
                                <SpInput label="Адрес" value={draft.address_text} onChange={(value) => setDraft({ ...draft, address_text: value })} span />
                                <SpInput label="Заметки" value={draft.notes} onChange={(value) => setDraft({ ...draft, notes: value })} span />
                            </div>
                        </div>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">🏷️</span><h4>Коммерческие отправители</h4><span className="hint">юрлицо заказа → кабинет и точка Новой Почты</span></div>
                            <div className={senderProfilesReady?.ready ? "sp-callout" : "sp-callout warn"}>
                                {senderProfilesReady ? (senderProfilesReady.ready ? "Все профили готовы к отправке." : (senderProfilesReady.profiles?.length ? "Есть неготовые профили — проверьте точку Новой Почты." : "Профили не заданы: отправка пойдёт через standard-точку юрлица заказа.")) : "Проверяю готовность профилей…"}
                            </div>
                            <div className="sp-table-wrap"><table className="sp-table"><thead><tr><th>Юрлицо</th><th>Точка НП</th><th>Статус</th><th></th></tr></thead><tbody>
                                {(selected.sender_profiles || []).map((item) => <tr key={item.id}><td>{item.business_organization_name || `#${item.business_organization_id}`}</td><td>{item.sender_point_name || `#${item.nova_poshta_sender_point_id}`}</td><td><span className={item.is_ready ? "sp-pill g" : "sp-pill warn"}>{item.is_active ? (item.is_ready ? "готов" : "нужна настройка") : "выключен"}</span>{item.readiness_error && <div className="hint">{item.readiness_error}</div>}</td><td><button type="button" className="sp-btn sp-btn-ghost" onClick={() => toggleSenderProfile(item)} disabled={busy}>{item.is_active ? "Выключить" : "Включить"}</button></td></tr>)}
                                {(selected.sender_profiles || []).length === 0 && <tr><td colSpan="4">Профили не заданы: будет использована standard-точка юрлица заказа.</td></tr>}
                            </tbody></table></div>
                            <div className="sp-grid3" style={{ marginTop: 12 }}>
                                <SpSelect label="Юрлицо заказа" value={senderProfileDraft.business_organization_id} onChange={(value) => setSenderProfileDraft({ business_organization_id: value, nova_poshta_sender_point_id: "" })} options={senderProfileOrgOptions} />
                                <SpSelect label="Точка Новой Почты" value={senderProfileDraft.nova_poshta_sender_point_id} onChange={(value) => setSenderProfileDraft({ ...senderProfileDraft, nova_poshta_sender_point_id: value })} options={senderPointOptions(senderProfilePrereq?.sender_points)} disabled={!senderProfileDraft.business_organization_id} />
                                <div className="sp-field"><label>&nbsp;</label><button type="button" className="sp-btn sp-btn-ghost" onClick={saveSenderProfile} disabled={busy || !senderProfileDraft.business_organization_id || !senderProfileDraft.nova_poshta_sender_point_id}>Сохранить профиль</button></div>
                            </div>
                        </div>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">₴</span><h4>Цены по умолчанию</h4><span className="hint">применяются при проведении накладных и переоценке</span></div>
                            <div className="sp-grid3">
                                <SpInput label="Наценка по умолчанию, %" type="number" value={draft.default_markup_percent} onChange={(value) => setDraft({ ...draft, default_markup_percent: value })} />
                                <SpSelect label="Округление цены" value={draft.price_rounding_mode} onChange={(value) => setDraft({ ...draft, price_rounding_mode: value })} options={ROUNDING_OPTIONS} placeholder={null} />
                                <SpCheck label="Публиковать офферы с нулевым остатком" checked={draft.emit_zero_stock_offers} onChange={(value) => setDraft({ ...draft, emit_zero_stock_offers: value })} />
                            </div>
                        </div>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">🚚</span><h4>Отправитель</h4><span className="hint">запись в реестре поставщиков (dropship_suppliers)</span></div>
                            <div className="sp-grid3">
                                <SpInput label="Приоритет отправителя" type="number" value={draft.sender_priority} onChange={(value) => setDraft({ ...draft, sender_priority: value })} />
                                <SpInput label="Ярус публикации (tier)" type="number" value={draft.sender_publish_priority_tier} onChange={(value) => setDraft({ ...draft, sender_publish_priority_tier: value })} />
                                <SpInput label="ID поставщика в SalesDrive" type="number" value={draft.sender_salesdrive_supplier_id} onChange={(value) => setDraft({ ...draft, sender_salesdrive_supplier_id: value })} />
                                <SpInput label="Город отправки" value={draft.sender_city} onChange={(value) => setDraft({ ...draft, sender_city: value })} />
                                <SpCheck label="Отправитель активен" checked={draft.sender_is_active} onChange={(value) => setDraft({ ...draft, sender_is_active: value })} />
                            </div>
                        </div>
                        <div className="sp-save-bar">
                            <span className="note">Остаток {fmtQty(selected.stats?.on_hand_qty)} шт · резерв {fmtQty(selected.stats?.reserved_qty)} · стоимость {fmtNum(selected.stats?.stock_value)} ₴</span>
                            <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setDraft(profileDraftFrom(selected))} disabled={busy}>Сбросить</button>
                            <button type="button" className="sp-btn sp-btn-primary" onClick={saveProfile} disabled={busy}>Сохранить</button>
                        </div>
                    </div>
                )}
                {cardTab === "create" && (
                    <div className="sp-panel" style={{ paddingTop: 0 }}>
                        <h3>Новый склад</h3>
                        <p className="lead">Выберите юрлицо — мастер проверит предпосылки и предложит следующий код ряда D. Склад создаётся вместе с отправителем и привязками к магазинам/маркетплейсам.</p>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">1</span><h4>Юрлицо и предпосылки</h4></div>
                            {createPrereqState === "loading" && !createPrereq && (
                                <div className="sp-msg">Загружаю список юрлиц…</div>
                            )}
                            {createPrereqState === "error" && (
                                <div className="sp-msg err ow-retry-msg">
                                    <span>Не удалось загрузить список юрлиц: сервер не ответил (обычно это кратковременный обрыв связи).</span>
                                    <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => setCreatePrereqAttempt((value) => value + 1)}>Повторить</button>
                                </div>
                            )}
                            <div className="sp-grid3">
                                <SpSelect label="Юрлицо" value={createDraft.business_organization_id} onChange={(value) => setCreateDraft({ ...createDraft, business_organization_id: value, nova_poshta_sender_point_id: "" })} options={legalEntityOptions} />
                                <SpInput label="Код склада (отправителя)" value={createDraft.code} onChange={(value) => setCreateDraft({ ...createDraft, code: value })} placeholder={createPrereq?.next_code || "D19"} />
                                <SpInput label="Название" value={createDraft.name} onChange={(value) => setCreateDraft({ ...createDraft, name: value })} />
                            </div>
                            {orgChosen && (
                                <div className="ow-checklist" style={{ marginTop: 14 }}>
                                    {checklist.map((item) => <span key={item.label} className={item.ok ? "sp-pill g" : "sp-pill warn"}>{item.ok ? "✓" : "!"} {item.label}</span>)}
                                </div>
                            )}
                        </div>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">2</span><h4>Отправка и цены</h4></div>
                            <div className="sp-grid3">
                                <SpSelect label="Точка отправки Новой Почты" value={createDraft.nova_poshta_sender_point_id} onChange={(value) => setCreateDraft({ ...createDraft, nova_poshta_sender_point_id: value })} options={senderPointOptions(createPrereq?.sender_points)} disabled={!orgChosen} />
                                <SpInput label="ID поставщика в SalesDrive" type="number" value={createDraft.salesdrive_supplier_id} onChange={(value) => setCreateDraft({ ...createDraft, salesdrive_supplier_id: value })} placeholder="необязательно" />
                                <SpInput label="Город отправки" value={createDraft.city} onChange={(value) => setCreateDraft({ ...createDraft, city: value })} />
                                <SpInput label="Наценка по умолчанию, %" type="number" value={createDraft.default_markup_percent} onChange={(value) => setCreateDraft({ ...createDraft, default_markup_percent: value })} />
                                <SpSelect label="Округление цены" value={createDraft.price_rounding_mode} onChange={(value) => setCreateDraft({ ...createDraft, price_rounding_mode: value })} options={ROUNDING_OPTIONS} placeholder={null} />
                                <SpInput label="Ярус публикации (tier)" type="number" value={createDraft.publish_priority_tier} onChange={(value) => setCreateDraft({ ...createDraft, publish_priority_tier: value })} />
                                <SpInput label="Приоритет отправителя" type="number" value={createDraft.priority} onChange={(value) => setCreateDraft({ ...createDraft, priority: value })} />
                                <SpCheck label="Остатки публикуются" checked={createDraft.stock_enabled} onChange={(value) => setCreateDraft({ ...createDraft, stock_enabled: value })} />
                                <SpCheck label="Участвует в заказах" checked={createDraft.orders_enabled} onChange={(value) => setCreateDraft({ ...createDraft, orders_enabled: value })} />
                            </div>
                        </div>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">3</span><h4>Магазины и маркетплейсы</h4><span className="hint">куда публикуются офферы склада</span></div>
                            <div className="sp-grid3">
                                <SpSelect label="Магазины Tabletki" value={createDraft.store_mode} onChange={(value) => setCreateDraft({ ...createDraft, store_mode: value })} options={[{ value: "all", label: "Все активные магазины" }, { value: "selected", label: "Выбрать вручную" }]} placeholder={null} />
                                <SpCheck label="Цена как РРЦ в магазинах (store_is_rrp)" checked={createDraft.store_is_rrp} onChange={(value) => setCreateDraft({ ...createDraft, store_is_rrp: value })} />
                            </div>
                            {createDraft.store_mode === "selected" && (
                                <div className="ow-multi" style={{ marginTop: 12 }}>
                                    {(createPrereq?.stores || []).map((store) => (
                                        <SpCheck key={store.id} label={`${store.store_code} · ${store.store_name}${store.is_active ? "" : " (неактивен)"}`} checked={createDraft.store_ids.includes(store.id)} onChange={() => toggleId("store_ids", store.id)} />
                                    ))}
                                    {(createPrereq?.stores || []).length === 0 && <span className="sp-hint">Магазины не найдены</span>}
                                </div>
                            )}
                            <div style={{ marginTop: 14 }}>
                                <div className="sp-hint" style={{ fontWeight: 700, marginBottom: 6 }}>Аккаунты маркетплейсов (Prom, Rozetka, Kasta)</div>
                                <div className="ow-multi">
                                    {(createPrereq?.marketplace_accounts || []).map((account) => (
                                        <SpCheck key={account.id} label={`${account.platform} · ${account.account_name || account.account_code}`} checked={createDraft.marketplace_account_ids.includes(account.id)} onChange={() => toggleId("marketplace_account_ids", account.id)} />
                                    ))}
                                    {(createPrereq?.marketplace_accounts || []).length === 0 && <span className="sp-hint">Аккаунты маркетплейсов не найдены</span>}
                                </div>
                            </div>
                        </div>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">4</span><h4>Прочее</h4></div>
                            <div className="sp-grid2">
                                <SpInput label="Адрес" value={createDraft.address_text} onChange={(value) => setCreateDraft({ ...createDraft, address_text: value })} />
                                <SpInput label="Заметки" value={createDraft.notes} onChange={(value) => setCreateDraft({ ...createDraft, notes: value })} />
                            </div>
                        </div>
                        <div className="sp-save-bar">
                            <span className="note">{orgChosen ? "Создание зарезервирует код и создаст отправителя на выбранном юрлице." : "Сначала выберите юрлицо."}</span>
                            <button type="button" className="sp-btn sp-btn-primary" onClick={submitCreate} disabled={!canCreate}>Создать склад</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------
   Вкладка «Остатки и партии»
   ------------------------------------------------------------------ */
const STOCK_PAGE = 100;

const adjustmentInitial = (sku = "") => ({ sku, qty_delta: "", reason: "inventory_count", unit_cost: "", expiration_date: "", comment: "" });

const StockTab = ({ warehouse, onReprice, onCreateWriteoff }) => {
    const [query, setQuery] = useState("");
    const [inStockOnly, setInStockOnly] = useState(true);
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState({ total: 0, items: [] });
    const [valuation, setValuation] = useState(null);
    const [selected, setSelected] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [batches, setBatches] = useState({});
    const [adjustment, setAdjustment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const load = useCallback(async (nextOffset = offset) => {
        if (!warehouse) return;
        setLoading(true);
        setError("");
        try {
            const [stock, value] = await Promise.all([
                getOwnWarehouseStock(warehouse.id, { q: query || undefined, in_stock_only: inStockOnly, limit: STOCK_PAGE, offset: nextOffset }),
                getOwnWarehouseValuation(warehouse.id).catch(() => null),
            ]);
            setData({ total: stock?.total || 0, items: stock?.items || [] });
            setValuation(value);
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить остатки"));
        } finally {
            setLoading(false);
        }
    }, [warehouse, query, inStockOnly, offset]);

    useEffect(() => {
        setOffset(0);
        setSelected([]);
        setExpanded({});
        setBatches({});
    }, [warehouse?.id]);
    useEffect(() => { load(offset); }, [load, offset]);

    const toggleExpand = async (sku) => {
        const next = !expanded[sku];
        setExpanded((prev) => ({ ...prev, [sku]: next }));
        if (next && !batches[sku]) {
            try {
                const rows = await getOwnWarehouseSkuBatches(warehouse.id, sku);
                setBatches((prev) => ({ ...prev, [sku]: rows }));
            } catch (err) {
                setError(apiError(err, `Не удалось загрузить партии ${sku}`));
            }
        }
    };
    const toggleSelected = (sku) => setSelected((prev) => (prev.includes(sku) ? prev.filter((item) => item !== sku) : [...prev, sku]));
    const allOnPage = data.items.length > 0 && data.items.every((item) => selected.includes(item.sku));
    const toggleAll = () => setSelected(allOnPage ? selected.filter((sku) => !data.items.some((item) => item.sku === sku)) : Array.from(new Set([...selected, ...data.items.map((item) => item.sku)])));
    const exportStock = async () => {
        if (!warehouse) return;
        setExporting(true);
        setError("");
        try {
            await downloadOwnWarehouseStock(
                warehouse.id,
                { q: query || undefined, in_stock_only: inStockOnly },
                `Остатки_${warehouse.code}.xlsx`,
            );
        } catch (err) {
            setError(apiError(err, "Не удалось сформировать Excel-файл"));
        } finally {
            setExporting(false);
        }
    };

    const submitAdjustment = async () => {
        if (!adjustment) return;
        const qty = Number(adjustment.qty_delta);
        if (!adjustment.sku.trim() || !qty) {
            setError("Укажите SKU и ненулевое количество");
            return;
        }
        if (qty > 0 && adjustment.unit_cost === "") {
            setError("Для прихода (+) нужна себестоимость партии");
            return;
        }
        setError("");
        setMessage("");
        try {
            await createOwnWarehouseAdjustment(warehouse.id, {
                sku: adjustment.sku.trim(),
                qty_delta: qty,
                reason: adjustment.reason,
                unit_cost: toNumberOrNull(adjustment.unit_cost),
                expiration_date: adjustment.expiration_date || null,
                comment: adjustment.comment || null,
            });
            setMessage(`Корректировка ${qty > 0 ? "+" : ""}${qty} по ${adjustment.sku} проведена`);
            setAdjustment(null);
            setBatches((prev) => ({ ...prev, [adjustment.sku.trim()]: undefined }));
            await load(offset);
        } catch (err) {
            setError(apiError(err, "Не удалось провести корректировку"));
        }
    };

    if (!warehouse) return <EmptyWarehouse />;
    const pageCount = Math.max(1, Math.ceil((data.total || 0) / STOCK_PAGE));
    const page = Math.floor(offset / STOCK_PAGE) + 1;

    return (
        <div className="sp-section">
            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>Остатки и партии · {warehouse.code}</h2>
                        <p>Снапшот по SKU: остаток, резерв, доступно, себестоимость FIFO/средняя, базовая цена. Раскройте строку, чтобы увидеть партии.</p>
                    </div>
                    <div className="sp-toolbar-actions">
                        <button type="button" className="sp-btn sp-btn-ghost" onClick={exportStock} disabled={exporting || loading}>{exporting ? "Формирую Excel…" : "Скачать Excel"}</button>
                        <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setAdjustment(adjustmentInitial(selected.length === 1 ? selected[0] : ""))}>Корректировка ±</button>
                        <button type="button" className="sp-btn sp-btn-ghost" disabled={!selected.length} onClick={() => onCreateWriteoff(data.items.filter((item) => selected.includes(item.sku) && Number(item.on_hand_qty) > 0).map((item) => ({ sku: item.sku, qty: Number(item.on_hand_qty) })))}>Списать весь остаток выбранных</button>
                        <button type="button" className="sp-btn sp-btn-primary" disabled={!selected.length} onClick={() => onReprice(selected)}>Переоценить выбранные{selected.length ? ` (${selected.length})` : ""}</button>
                    </div>
                </div>
                <div className="sp-stats">
                    <span className="sp-stat">SKU <span>{fmtQty(valuation?.sku_count ?? warehouse.stats?.sku_in_stock)}</span></span>
                    <span className="sp-stat">Остаток <span>{fmtQty(valuation?.total_qty ?? warehouse.stats?.on_hand_qty)} шт</span></span>
                    <span className="sp-stat">Резерв <span>{fmtQty(warehouse.stats?.reserved_qty)} шт</span></span>
                    <span className="sp-stat">Стоимость <span>{fmtNum(valuation?.total_value ?? warehouse.stats?.stock_value)} ₴</span></span>
                </div>
                <div className="sp-filters">
                    <SpInput label="Поиск (SKU или название)" value={query} onChange={setQuery} placeholder="введите и нажмите «Найти»" />
                    <SpCheck label="Только с остатком" checked={inStockOnly} onChange={setInStockOnly} />
                    <button type="button" className="sp-btn sp-btn-primary" onClick={() => { setOffset(0); load(0); }} disabled={loading}>Найти</button>
                </div>
                <Msg error={error} message={message} />
            </div>

            {adjustment && (
                <div className="sp-card sp-panel">
                    <h3>Корректировка остатка ±</h3>
                    <p className="lead">Плюс создаёт новую партию по указанной себестоимости; минус списывает FIFO. Причина попадает в журнал движений.</p>
                    <div className="sp-grid3">
                        <SpInput label="SKU" value={adjustment.sku} onChange={(value) => setAdjustment({ ...adjustment, sku: value })} />
                        <SpInput label="Количество (±)" type="number" value={adjustment.qty_delta} onChange={(value) => setAdjustment({ ...adjustment, qty_delta: value })} placeholder="например 3 или -2" />
                        <SpSelect label="Причина" value={adjustment.reason} onChange={(value) => setAdjustment({ ...adjustment, reason: value })} options={ADJUSTMENT_REASONS} placeholder={null} />
                        <SpInput label={Number(adjustment.qty_delta) > 0 ? "Себестоимость, ₴ (обязательно для прихода)" : "Себестоимость, ₴"} type="number" value={adjustment.unit_cost} onChange={(value) => setAdjustment({ ...adjustment, unit_cost: value })} />
                        <SpInput label="Срок годности" type="date" value={adjustment.expiration_date} onChange={(value) => setAdjustment({ ...adjustment, expiration_date: value })} />
                        <SpInput label="Комментарий" value={adjustment.comment} onChange={(value) => setAdjustment({ ...adjustment, comment: value })} />
                    </div>
                    <div className="sp-save-bar">
                        <span className="note">Изменение сразу попадает в остатки и журнал.</span>
                        <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setAdjustment(null)}>Отмена</button>
                        <button type="button" className="sp-btn sp-btn-primary" onClick={submitAdjustment}>Провести корректировку</button>
                    </div>
                </div>
            )}

            <div className="sp-tbl-wrap">
                <table className="sp-tbl">
                    <thead>
                        <tr>
                            <th className="ow-ck"><input type="checkbox" aria-label="Выбрать все" checked={allOnPage} onChange={toggleAll} /></th>
                            <th>SKU</th><th>Название</th><th>Остаток</th><th>Резерв</th><th>Доступно</th><th>С/с FIFO</th><th>С/с средняя</th><th>Базовая цена</th><th>Источник</th><th>Последнее движение</th><th>Партии</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((item) => (
                            <React.Fragment key={item.sku}>
                                <tr className={selected.includes(item.sku) ? "sel" : undefined}>
                                    <td className="ow-ck"><input type="checkbox" aria-label={`Выбрать ${item.sku}`} checked={selected.includes(item.sku)} onChange={() => toggleSelected(item.sku)} /></td>
                                    <td className="sp-strong">{item.sku}</td>
                                    <td>{item.name || "—"}</td>
                                    <td>{fmtQty(item.on_hand_qty)}</td>
                                    <td>{fmtQty(item.reserved_qty)}</td>
                                    <td className={Number(item.available_qty) > 0 ? "ow-pos" : undefined}>{fmtQty(item.available_qty)}</td>
                                    <td>{fmtNum(item.current_fifo_cost)}</td>
                                    <td>{fmtNum(item.avg_cost)}</td>
                                    <td className="sp-strong">{fmtNum(item.base_retail_price)}</td>
                                    <td>{item.base_retail_price_source ? <span className="sp-pill g">{priceSourceLabel(item.base_retail_price_source)}</span> : <span className="sp-pill warn">нет цены</span>}</td>
                                    <td className="sp-mini">{fmtDateTime(item.last_movement_at)}</td>
                                    <td>
                                        <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => toggleExpand(item.sku)}>
                                            {expanded[item.sku] ? "Скрыть" : `Партии${item.open_batches != null ? ` (${item.open_batches})` : ""}`}
                                        </button>
                                    </td>
                                </tr>
                                {expanded[item.sku] && (
                                    <tr className="ow-sub">
                                        <td colSpan={12}>
                                            {!batches[item.sku] ? <span className="sp-hint">Загрузка партий…</span> : (
                                                <table className="sp-tbl">
                                                    <thead><tr><th>Партия</th><th>Источник</th><th>Принята</th><th>Принято</th><th>Остаток</th><th>С/с с НДС</th><th>С/с без НДС</th><th>НДС %</th><th>Срок</th><th>Поставщик</th><th>Накладная</th><th>Статус</th></tr></thead>
                                                    <tbody>
                                                        {batches[item.sku].map((batch) => (
                                                            <tr key={batch.id}>
                                                                <td className="sp-strong">#{batch.id}</td>
                                                                <td>{batch.source_type}{batch.source_id ? ` #${batch.source_id}` : ""}</td>
                                                                <td className="sp-mini">{fmtDateTime(batch.received_at)}</td>
                                                                <td>{fmtQty(batch.qty_received)}</td>
                                                                <td className={Number(batch.qty_on_hand) > 0 ? "ow-pos" : undefined}>{fmtQty(batch.qty_on_hand)}</td>
                                                                <td>{fmtNum(batch.unit_cost)}</td>
                                                                <td>{fmtNum(batch.unit_cost_ex_vat)}</td>
                                                                <td>{batch.vat_rate ?? "—"}</td>
                                                                <td className="sp-mini">{fmtDate(batch.expiration_date)}</td>
                                                                <td>{batch.supplier_name || "—"}</td>
                                                                <td>{batch.invoice_number || (batch.invoice_id ? `#${batch.invoice_id}` : "—")}</td>
                                                                <td><span className={batch.status === "open" ? "sp-pill g" : "sp-pill off"}>{batch.status}</span></td>
                                                            </tr>
                                                        ))}
                                                        {batches[item.sku].length === 0 && <tr><td className="sp-mini" colSpan={12}>Партий нет</td></tr>}
                                                    </tbody>
                                                </table>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        {!loading && data.items.length === 0 && <tr><td className="sp-mini" colSpan={12}>Остатков нет — проведите накладную или корректировку «+»</td></tr>}
                    </tbody>
                </table>
            </div>
            <div className="ow-pager">
                <span className="sp-hint">Всего {data.total} · страница {page} из {pageCount}</span>
                <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - STOCK_PAGE))}>‹ Назад</button>
                <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" disabled={offset + STOCK_PAGE >= data.total || loading} onClick={() => setOffset(offset + STOCK_PAGE)}>Вперёд ›</button>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------
   Вкладка «Движения»: журнал + возврат от покупателя + резервы
   ------------------------------------------------------------------ */
const LEDGER_PAGE = 50;

const MovementsTab = ({ warehouse }) => {
    const [filters, setFilters] = useState({ sku: "", event_type: "", date_from: "", date_to: "", order_ref: "" });
    const [offset, setOffset] = useState(0);
    const [ledger, setLedger] = useState({ total: 0, items: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [reservationStatus, setReservationStatus] = useState("active");
    const [reservations, setReservations] = useState([]);

    const loadLedger = useCallback(async (nextOffset) => {
        if (!warehouse) return;
        setLoading(true);
        setError("");
        try {
            const data = await getOwnWarehouseLedger(warehouse.id, { ...filters, limit: LEDGER_PAGE, offset: nextOffset });
            setLedger({ total: data?.total || 0, items: data?.items || [] });
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить журнал"));
        } finally {
            setLoading(false);
        }
    }, [warehouse, filters]);

    const loadReservations = useCallback(async () => {
        if (!warehouse) return;
        try {
            const rows = await listOwnWarehouseReservations(warehouse.id, { status: reservationStatus || undefined, limit: 100 });
            setReservations(Array.isArray(rows) ? rows : (rows?.items || []));
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить резервы"));
        }
    }, [warehouse, reservationStatus]);

    useEffect(() => { setOffset(0); }, [warehouse?.id]);
    useEffect(() => { loadLedger(offset); }, [loadLedger, offset]);
    useEffect(() => { loadReservations(); }, [loadReservations]);

    if (!warehouse) return <EmptyWarehouse />;
    const setF = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
    const deltaCell = (value) => {
        const number = Number(value || 0);
        if (!number) return <span className="sp-mini">0</span>;
        return <span className={number > 0 ? "ow-pos" : "ow-neg"}>{number > 0 ? `+${fmtQty(number)}` : fmtQty(number)}</span>;
    };

    return (
        <div className="sp-section">
            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>Движения · {warehouse.code}</h2>
                        <p>Журнал всех изменений остатка и резерва: проведения, корректировки, резервы под заказ, списания при отправке, возвраты.</p>
                    </div>
                    <div className="sp-toolbar-actions">
                        <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => { setFilters({ sku: "", event_type: "", date_from: "", date_to: "", order_ref: "" }); setOffset(0); }}>Сбросить фильтры</button>
                    </div>
                </div>
                <div className="sp-grid5">
                    <SpInput label="SKU" value={filters.sku} onChange={(value) => setF({ sku: value })} />
                    <SpSelect label="Тип события" value={filters.event_type} onChange={(value) => setF({ event_type: value })} options={EVENT_TYPES} placeholder="Все" />
                    <SpInput label="С даты" type="date" value={filters.date_from} onChange={(value) => setF({ date_from: value })} />
                    <SpInput label="По дату" type="date" value={filters.date_to} onChange={(value) => setF({ date_to: value })} />
                    <SpInput label="Заказ (order_ref)" value={filters.order_ref} onChange={(value) => setF({ order_ref: value })} />
                </div>
                <Msg error={error} />
            </div>
            <div className="sp-tbl-wrap">
                <table className="sp-tbl">
                    <thead><tr><th>Когда</th><th>Событие</th><th>SKU</th><th>Название</th><th>Δ остаток</th><th>Δ резерв</th><th>С/с</th><th>Партия</th><th>Документ</th><th>Заказ</th><th>Причина / комментарий</th><th>Кто</th></tr></thead>
                    <tbody>
                        {ledger.items.map((row) => (
                            <tr key={row.id}>
                                <td className="sp-mini">{fmtDateTime(row.created_at)}</td>
                                <td><span className="sp-pill">{eventLabel(row.event_type)}</span></td>
                                <td className="sp-strong">{row.sku}</td>
                                <td>{row.name || "—"}</td>
                                <td>{deltaCell(row.qty_delta)}</td>
                                <td>{deltaCell(row.reserved_delta)}</td>
                                <td>{fmtNum(row.unit_cost)}</td>
                                <td>{row.batch_id ? `#${row.batch_id}` : "—"}</td>
                                <td>{row.writeoff_document_number || "—"}</td>
                                <td>{row.order_ref || "—"}{row.line_key ? <span className="sp-mini"> · {row.line_key}</span> : null}</td>
                                <td className="sp-mini">{[row.reason, row.comment].filter(Boolean).join(" · ") || "—"}</td>
                                <td className="sp-mini">{row.created_by || "—"}</td>
                            </tr>
                        ))}
                        {!loading && ledger.items.length === 0 && <tr><td className="sp-mini" colSpan={11}>Движений не найдено</td></tr>}
                    </tbody>
                </table>
            </div>
            <div className="ow-pager">
                <span className="sp-hint">Всего {ledger.total}</span>
                <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - LEDGER_PAGE))}>‹ Назад</button>
                <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" disabled={offset + LEDGER_PAGE >= ledger.total || loading} onClick={() => setOffset(offset + LEDGER_PAGE)}>Вперёд ›</button>
            </div>

            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>Резервы</h2>
                        <p>Резерв ставится при приёме заказа и либо списывается при отправке (11/12), либо снимается (6/17/8) или истекает по TTL.</p>
                    </div>
                    <div className="sp-toolbar-actions">
                        <SpSelect label="Статус" value={reservationStatus} onChange={setReservationStatus} options={RESERVATION_STATUSES} placeholder="Все" />
                    </div>
                </div>
                <div className="sp-tbl-wrap">
                    <table className="sp-tbl">
                        <thead><tr><th>ID</th><th>SKU</th><th>Название</th><th>Кол-во</th><th>Канал</th><th>Заказ</th><th>SalesDrive</th><th>Статус</th><th>Зарезервирован</th><th>Снят / списан</th><th>Причина снятия</th><th>С/с оценка / факт</th></tr></thead>
                        <tbody>
                            {reservations.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.id}</td>
                                    <td className="sp-strong">{row.sku}</td>
                                    <td>{row.name || "—"}</td>
                                    <td>{fmtQty(row.qty)}</td>
                                    <td>{row.channel || "—"}</td>
                                    <td>{row.order_ref || row.external_id || "—"}</td>
                                    <td>{row.salesdrive_order_id || "—"}</td>
                                    <td><span className={row.status === "active" ? "sp-pill g" : row.status === "written_off" ? "sp-pill" : "sp-pill off"}>{row.status}</span></td>
                                    <td className="sp-mini">{fmtDateTime(row.reserved_at)}</td>
                                    <td className="sp-mini">{fmtDateTime(row.written_off_at || row.released_at)}</td>
                                    <td className="sp-mini">{row.release_reason || "—"}</td>
                                    <td>{fmtNum(row.estimated_unit_cost)} / {fmtNum(row.actual_unit_cost)}</td>
                                </tr>
                            ))}
                            {reservations.length === 0 && <tr><td className="sp-mini" colSpan={12}>Резервов нет</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------
   Вкладка «Цены / переоценка» + проверка публикации
   ------------------------------------------------------------------ */
const PricingTab = ({ warehouse, initialSkus }) => {
    const [skus, setSkus] = useState(initialSkus || []);
    const [skuInput, setSkuInput] = useState("");
    const [mode, setMode] = useState("markup_fifo");
    const [markup, setMarkup] = useState(warehouse?.default_markup_percent ?? "");
    const [rounding, setRounding] = useState(warehouse?.price_rounding_mode || "none");
    const [comment, setComment] = useState("");
    const [manualPrices, setManualPrices] = useState({});
    const [preview, setPreview] = useState(null);
    const [runs, setRuns] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [offersReport, setOffersReport] = useState(null);
    const [offersBusy, setOffersBusy] = useState(false);

    useEffect(() => {
        setSkus(initialSkus || []);
        setPreview(null);
    }, [initialSkus]);
    useEffect(() => {
        setMarkup(warehouse?.default_markup_percent ?? "");
        setRounding(warehouse?.price_rounding_mode || "none");
        setPreview(null);
        setOffersReport(null);
    }, [warehouse?.id, warehouse?.default_markup_percent, warehouse?.price_rounding_mode]);

    const loadRuns = useCallback(async () => {
        if (!warehouse) return;
        try {
            const rows = await listOwnWarehouseRepricingRuns(warehouse.id, { limit: 20 });
            setRuns(Array.isArray(rows) ? rows : (rows?.items || []));
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить историю переоценок"));
        }
    }, [warehouse]);
    useEffect(() => { loadRuns(); }, [loadRuns]);

    const addSkus = () => {
        const parts = skuInput.split(/[,\s;]+/).map((item) => item.trim()).filter(Boolean);
        if (!parts.length) return;
        setSkus((prev) => Array.from(new Set([...prev, ...parts])));
        setSkuInput("");
        setPreview(null);
    };
    const payload = () => ({
        skus: skus.length ? skus : null,
        mode,
        markup_percent: mode === "manual" ? null : toNumberOrNull(markup),
        manual_prices: mode === "manual" ? Object.fromEntries(skus.filter((sku) => manualPrices[sku] !== undefined && manualPrices[sku] !== "").map((sku) => [sku, Number(manualPrices[sku])])) : null,
        rounding_mode: rounding,
        comment: comment || null,
    });
    const runPreview = async () => {
        if (mode === "manual" && !skus.length) {
            setError("Для ручной цены укажите SKU");
            return;
        }
        setBusy(true);
        setError("");
        setMessage("");
        try {
            setPreview(await previewOwnWarehouseRepricing(warehouse.id, payload()));
        } catch (err) {
            setError(apiError(err, "Не удалось построить предпросмотр"));
        } finally {
            setBusy(false);
        }
    };
    const apply = async () => {
        if (!preview?.preview_hash) return;
        if (!window.confirm(`Применить новые цены для ${preview.items?.length || 0} SKU?`)) return;
        setBusy(true);
        setError("");
        try {
            const result = await applyOwnWarehouseRepricing(warehouse.id, { ...payload(), preview_hash: preview.preview_hash });
            setMessage(`Переоценка применена: ${result?.items_count ?? result?.items?.length ?? preview.items?.length ?? 0} SKU`);
            setPreview(null);
            await loadRuns();
        } catch (err) {
            if (err?.response?.status === 409) {
                setError("Предпросмотр устарел (остатки или цены изменились) — постройте его заново");
            } else {
                setError(apiError(err, "Не удалось применить переоценку"));
            }
        } finally {
            setBusy(false);
        }
    };
    const runOffers = async (refresh) => {
        setOffersBusy(true);
        setError("");
        setMessage("");
        try {
            const report = refresh ? await refreshOwnWarehouseOffers(warehouse.id) : await dryRunOwnWarehouseOffers(warehouse.id);
            setOffersReport({ kind: refresh ? "refresh" : "dry_run", report });
            if (refresh) setMessage("Офферы склада пересобраны");
        } catch (err) {
            setError(apiError(err, refresh ? "Не удалось пересобрать офферы" : "Не удалось выполнить dry-run"));
        } finally {
            setOffersBusy(false);
        }
    };

    if (!warehouse) return <EmptyWarehouse />;
    const deltaClass = (value) => (Number(value) > 0 ? "sp-delta good" : Number(value) < 0 ? "sp-delta bad" : undefined);
    const links = Array.isArray(offersReport?.report?.links) ? offersReport.report.links : [];

    return (
        <div className="sp-section">
            <div className="sp-card sp-panel">
                <h3>Переоценка · {warehouse.code}</h3>
                <p className="lead">Базовая розничная цена едина для всех магазинов склада; надстройки магазина (РРЦ, наценки) применяются поверх. Без SKU переоцениваются все позиции с остатком.</p>
                <Msg error={error} message={message} />
                <div className="sp-block">
                    <div className="sp-bh"><span className="ic">#</span><h4>Позиции</h4><span className="hint">{skus.length ? `${skus.length} SKU` : "все с остатком"}</span></div>
                    <div className="ow-chips" style={{ marginBottom: 10 }}>
                        {skus.map((sku) => <span key={sku} className="ow-chip">{sku}<button type="button" aria-label={`Убрать ${sku}`} onClick={() => { setSkus(skus.filter((item) => item !== sku)); setPreview(null); }}>×</button></span>)}
                        {skus.length === 0 && <span className="sp-pill g">все SKU с остатком</span>}
                        {skus.length > 0 && <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => { setSkus([]); setPreview(null); }}>Очистить</button>}
                    </div>
                    <div className="sp-filters">
                        <SpInput label="Добавить SKU (через запятую или пробел)" value={skuInput} onChange={setSkuInput} />
                        <div />
                        <button type="button" className="sp-btn sp-btn-ghost" onClick={addSkus} disabled={!skuInput.trim()}>Добавить</button>
                    </div>
                </div>
                <div className="sp-block">
                    <div className="sp-bh"><span className="ic">%</span><h4>Правило</h4></div>
                    <div className="sp-grid3">
                        <SpSelect label="Режим" value={mode} onChange={(value) => { setMode(value); setPreview(null); }} options={REPRICING_MODES} placeholder={null} />
                        <SpInput label="Наценка, %" type="number" value={markup} onChange={(value) => { setMarkup(value); setPreview(null); }} disabled={mode === "manual"} />
                        <SpSelect label="Округление" value={rounding} onChange={(value) => { setRounding(value); setPreview(null); }} options={ROUNDING_OPTIONS} placeholder={null} />
                        <SpInput label="Комментарий" value={comment} onChange={setComment} span />
                    </div>
                    {mode === "manual" && skus.length > 0 && (
                        <div className="sp-tbl-wrap" style={{ marginTop: 12 }}>
                            <table className="sp-tbl">
                                <thead><tr><th>SKU</th><th>Новая цена, ₴</th></tr></thead>
                                <tbody>
                                    {skus.map((sku) => (
                                        <tr key={sku}>
                                            <td className="sp-strong">{sku}</td>
                                            <td style={{ width: 200 }}><input type="number" className="sp-inp" aria-label={`Цена ${sku}`} value={manualPrices[sku] ?? ""} onChange={(event) => { setManualPrices({ ...manualPrices, [sku]: event.target.value }); setPreview(null); }} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div className="sp-save-bar">
                    <span className="note">Применение возможно только после предпросмотра; если данные изменились — предпросмотр нужно повторить.</span>
                    <button type="button" className="sp-btn sp-btn-ghost" onClick={runPreview} disabled={busy}>Предпросмотр</button>
                    <button type="button" className="sp-btn sp-btn-primary" onClick={apply} disabled={busy || !preview?.preview_hash || !(preview?.items || []).length}>Применить</button>
                </div>
                {preview && (
                    <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
                        <div className="sp-stats">
                            <span className="sp-stat">К изменению <span>{(preview.items || []).length}</span></span>
                            <span className={(preview.skipped || []).length ? "sp-stat warn" : "sp-stat zero"}>Пропущено <span>{(preview.skipped || []).length}</span></span>
                        </div>
                        <div className="sp-tbl-wrap">
                            <table className="sp-tbl">
                                <thead><tr><th>SKU</th><th>Название</th><th>База (с/с)</th><th>Старая цена</th><th>Источник</th><th>Новая цена</th><th>Источник</th><th>Δ %</th></tr></thead>
                                <tbody>
                                    {(preview.items || []).map((item) => (
                                        <tr key={item.sku}>
                                            <td className="sp-strong">{item.sku}</td>
                                            <td>{item.name || "—"}</td>
                                            <td>{fmtNum(item.cost_basis)}{item.cost_basis_kind ? <span className="sp-mini"> · {item.cost_basis_kind}</span> : null}</td>
                                            <td>{fmtNum(item.old_price)}</td>
                                            <td className="sp-mini">{priceSourceLabel(item.old_source)}</td>
                                            <td className="sp-strong">{fmtNum(item.new_price)}</td>
                                            <td className="sp-mini">{priceSourceLabel(item.new_source)}</td>
                                            <td className={deltaClass(item.delta_percent)}>{item.delta_percent == null ? "—" : `${Number(item.delta_percent) > 0 ? "+" : ""}${fmtNum(item.delta_percent, 1)}%`}</td>
                                        </tr>
                                    ))}
                                    {(preview.items || []).length === 0 && <tr><td className="sp-mini" colSpan={8}>Нечего переоценивать</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        {(preview.skipped || []).length > 0 && (
                            <div className="sp-warnbox">{preview.skipped.map((item) => <span key={item.sku}>{item.sku}: {item.reason}</span>)}</div>
                        )}
                    </div>
                )}
            </div>

            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>История переоценок</h2>
                        <p>Последние 20 прогонов по складу.</p>
                    </div>
                </div>
                <div className="sp-tbl-wrap">
                    <table className="sp-tbl">
                        <thead><tr><th>ID</th><th>Когда</th><th>Режим</th><th>%</th><th>Округление</th><th>SKU</th><th>Кто</th><th>Комментарий</th></tr></thead>
                        <tbody>
                            {runs.map((run) => (
                                <tr key={run.id}>
                                    <td>{run.id}</td>
                                    <td className="sp-mini">{fmtDateTime(run.applied_at)}</td>
                                    <td>{REPRICING_MODES.find((item) => item.value === run.mode)?.label || run.mode}</td>
                                    <td>{run.markup_percent ?? "—"}</td>
                                    <td>{ROUNDING_OPTIONS.find((item) => item.value === run.rounding_mode)?.label || run.rounding_mode || "—"}</td>
                                    <td>{run.items_count}</td>
                                    <td className="sp-mini">{run.created_by || "—"}</td>
                                    <td className="sp-mini">{run.comment || "—"}</td>
                                </tr>
                            ))}
                            {runs.length === 0 && <tr><td className="sp-mini" colSpan={8}>Переоценок ещё не было</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>Проверка публикации</h2>
                        <p>Dry-run собирает офферы склада по магазинам без записи; «Пересобрать» записывает business_store_offers (учитывает флаги контура и publish_dry_run).</p>
                    </div>
                    <div className="sp-toolbar-actions">
                        <button type="button" className="sp-btn sp-btn-ghost" onClick={() => runOffers(false)} disabled={offersBusy}>Dry-run офферов</button>
                        <button type="button" className="sp-btn sp-btn-primary" onClick={() => runOffers(true)} disabled={offersBusy}>Пересобрать офферы</button>
                    </div>
                </div>
                {offersReport && (
                    <div style={{ display: "grid", gap: 10 }}>
                        <span className="sp-hint">{offersReport.kind === "dry_run" ? "Результат dry-run" : "Результат пересборки"}</span>
                        {links.length > 0 && (
                            <div className="sp-tbl-wrap">
                                <table className="sp-tbl">
                                    <thead><tr><th>Магазин</th><th>Статус</th><th>Строк</th><th>Предупреждения</th></tr></thead>
                                    <tbody>
                                        {links.map((link, index) => (
                                            <tr key={`${link.store_code || index}`}>
                                                <td className="sp-strong">{link.store_code || link.store_id || "—"}</td>
                                                <td><span className={link.status === "ok" || link.status === "built" ? "sp-pill g" : "sp-pill warn"}>{link.status || "—"}</span></td>
                                                <td>{link.built_rows ?? "—"}</td>
                                                <td className="sp-mini">{(link.warnings || []).join("; ") || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {links.some((link) => (link.sample_rows || []).length) && (
                            <pre className="ow-json">{JSON.stringify(links.flatMap((link) => (link.sample_rows || []).slice(0, 3)), null, 2)}</pre>
                        )}
                        {links.length === 0 && <pre className="ow-json">{JSON.stringify(offersReport.report, null, 2)}</pre>}
                    </div>
                )}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------
   Вкладка «Динамика»
   ------------------------------------------------------------------ */
const DynamicsTab = ({ warehouse }) => {
    const [range, setRange] = useState({ date_from: daysAgoIso(30), date_to: todayIso() });
    const [metrics, setMetrics] = useState([]);
    const [turnoverDays, setTurnoverDays] = useState("30");
    const [turnover, setTurnover] = useState([]);
    const [expiringDays, setExpiringDays] = useState("90");
    const [expiring, setExpiring] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const loadMetrics = useCallback(async () => {
        if (!warehouse) return;
        setError("");
        try {
            const rows = await getOwnWarehouseDailyMetrics(warehouse.id, range);
            setMetrics(Array.isArray(rows) ? rows : (rows?.items || []));
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить динамику"));
        }
    }, [warehouse, range]);
    const loadTurnover = useCallback(async () => {
        if (!warehouse) return;
        try {
            const rows = await getOwnWarehouseTurnover(warehouse.id, Number(turnoverDays) || 30);
            setTurnover(Array.isArray(rows) ? rows : (rows?.items || []));
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить оборачиваемость"));
        }
    }, [warehouse, turnoverDays]);
    const loadExpiring = useCallback(async () => {
        if (!warehouse) return;
        try {
            const rows = await getOwnWarehouseExpiring(warehouse.id, Number(expiringDays) || 90);
            setExpiring(Array.isArray(rows) ? rows : (rows?.items || []));
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить истекающие партии"));
        }
    }, [warehouse, expiringDays]);

    useEffect(() => { loadMetrics(); }, [loadMetrics]);
    useEffect(() => { loadTurnover(); }, [loadTurnover]);
    useEffect(() => { loadExpiring(); }, [loadExpiring]);

    const recompute = async () => {
        setBusy(true);
        setError("");
        setMessage("");
        try {
            await recomputeOwnWarehouseMetrics(warehouse.id, range);
            setMessage("Дневные метрики пересчитаны");
            await loadMetrics();
        } catch (err) {
            setError(apiError(err, "Не удалось пересчитать метрики"));
        } finally {
            setBusy(false);
        }
    };

    if (!warehouse) return <EmptyWarehouse />;
    const labels = metrics.map((row) => row.metric_date);
    const last = metrics[metrics.length - 1];
    const sum = (field) => metrics.reduce((acc, row) => acc + (Number(row[field]) || 0), 0);

    return (
        <div className="sp-section">
            <div className="sp-card sp-toolbar">
                <div className="sp-toolbar-head">
                    <div>
                        <h2>Динамика · {warehouse.code}</h2>
                        <p>Дневные снимки остатка и стоимости, приход/списание/возврат, оборачиваемость и партии с истекающим сроком.</p>
                    </div>
                    <div className="sp-toolbar-actions">
                        <SpInput label="С" type="date" value={range.date_from} onChange={(value) => setRange({ ...range, date_from: value })} />
                        <SpInput label="По" type="date" value={range.date_to} onChange={(value) => setRange({ ...range, date_to: value })} />
                        <button type="button" className="sp-btn sp-btn-ghost" onClick={recompute} disabled={busy}>Пересчитать за период</button>
                    </div>
                </div>
                <div className="sp-stats">
                    <span className="sp-stat">Остаток сейчас <span>{fmtQty(last?.on_hand_qty)} шт</span></span>
                    <span className="sp-stat">Стоимость <span>{fmtNum(last?.on_hand_value)} ₴</span></span>
                    <span className="sp-stat">Приход за период <span>{fmtQty(sum("receipts_qty"))} шт · {fmtNum(sum("receipts_value"))} ₴</span></span>
                    <span className="sp-stat">Списано <span>{fmtQty(sum("write_offs_qty"))} шт · {fmtNum(sum("write_offs_value"))} ₴</span></span>
                    <span className="sp-stat">Возвраты <span>{fmtQty(sum("returns_qty"))} шт</span></span>
                </div>
                <Msg error={error} message={message} />
                <div className="ow-charts">
                    <LineChart title="Остаток, шт" subtitle="on_hand_qty на конец дня" labels={labels} values={metrics.map((row) => row.on_hand_qty)} color="var(--ow-c1)" unit="шт" />
                    <LineChart title="Стоимость остатка, ₴" subtitle="on_hand_value по себестоимости партий" labels={labels} values={metrics.map((row) => row.on_hand_value)} color="var(--ow-c1)" unit="₴" />
                </div>
                <GroupedBarChart
                    title="Приход · списания · возвраты, шт"
                    subtitle="по дням"
                    labels={labels}
                    series={[
                        { key: "receipts", label: "Приход", color: "var(--ow-c1)", values: metrics.map((row) => row.receipts_qty) },
                        { key: "write_offs", label: "Списания", color: "var(--ow-c2)", values: metrics.map((row) => row.write_offs_qty) },
                        { key: "returns", label: "Возвраты", color: "var(--ow-c3)", values: metrics.map((row) => row.returns_qty) },
                    ]}
                />
                <details>
                    <summary className="sp-hint" style={{ cursor: "pointer" }}>Таблица дневных метрик ({metrics.length})</summary>
                    <div className="sp-tbl-wrap" style={{ marginTop: 10 }}>
                        <table className="sp-tbl">
                            <thead><tr><th>Дата</th><th>Остаток</th><th>Стоимость</th><th>Резерв</th><th>SKU всего</th><th>SKU в наличии</th><th>Приход</th><th>Списания</th><th>Возвраты</th><th>Корр. +</th><th>Корр. −</th></tr></thead>
                            <tbody>
                                {metrics.map((row) => (
                                    <tr key={row.metric_date}>
                                        <td className="sp-mini">{row.metric_date}</td>
                                        <td>{fmtQty(row.on_hand_qty)}</td>
                                        <td>{fmtNum(row.on_hand_value)}</td>
                                        <td>{fmtQty(row.reserved_qty)}</td>
                                        <td>{fmtQty(row.sku_total)}</td>
                                        <td>{fmtQty(row.sku_in_stock)}</td>
                                        <td>{fmtQty(row.receipts_qty)} · {fmtNum(row.receipts_value)}</td>
                                        <td>{fmtQty(row.write_offs_qty)} · {fmtNum(row.write_offs_value)}</td>
                                        <td>{fmtQty(row.returns_qty)} · {fmtNum(row.returns_value)}</td>
                                        <td>{fmtQty(row.adjustments_plus_qty)}</td>
                                        <td>{fmtQty(row.adjustments_minus_qty)}</td>
                                    </tr>
                                ))}
                                {metrics.length === 0 && <tr><td className="sp-mini" colSpan={11}>Нет метрик за период — нажмите «Пересчитать за период»</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </details>
            </div>

            <div className="sp-result-grid">
                <div className="sp-card sp-toolbar">
                    <div className="sp-toolbar-head">
                        <div>
                            <h2>Оборачиваемость</h2>
                            <p>Списания за окно и запас в днях при текущем темпе.</p>
                        </div>
                        <div className="sp-toolbar-actions">
                            <SpSelect label="Окно" value={turnoverDays} onChange={setTurnoverDays} options={[{ value: "7", label: "7 дней" }, { value: "30", label: "30 дней" }, { value: "90", label: "90 дней" }]} placeholder={null} />
                        </div>
                    </div>
                    <div className="sp-tbl-wrap">
                        <table className="sp-tbl">
                            <thead><tr><th>SKU</th><th>Название</th><th>Остаток</th><th>Списано</th><th>Запас, дней</th></tr></thead>
                            <tbody>
                                {turnover.map((row) => (
                                    <tr key={row.sku}>
                                        <td className="sp-strong">{row.sku}</td>
                                        <td>{row.name || "—"}</td>
                                        <td>{fmtQty(row.on_hand_qty)}</td>
                                        <td>{fmtQty(row.write_offs_qty)}</td>
                                        <td>{row.days_of_stock == null ? <span className="sp-mini">∞</span> : <span className={Number(row.days_of_stock) < 7 ? "ow-neg" : undefined}>{fmtNum(row.days_of_stock, 0)}</span>}</td>
                                    </tr>
                                ))}
                                {turnover.length === 0 && <tr><td className="sp-mini" colSpan={5}>Нет данных</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="sp-card sp-toolbar">
                    <div className="sp-toolbar-head">
                        <div>
                            <h2>Истекающие партии</h2>
                            <p>Открытые партии со сроком годности в горизонте.</p>
                        </div>
                        <div className="sp-toolbar-actions">
                            <SpSelect label="Горизонт" value={expiringDays} onChange={setExpiringDays} options={[{ value: "30", label: "30 дней" }, { value: "90", label: "90 дней" }, { value: "180", label: "180 дней" }]} placeholder={null} />
                        </div>
                    </div>
                    <div className="sp-tbl-wrap">
                        <table className="sp-tbl">
                            <thead><tr><th>Партия</th><th>SKU</th><th>Название</th><th>Остаток</th><th>Срок</th><th>Осталось дней</th></tr></thead>
                            <tbody>
                                {expiring.map((row) => (
                                    <tr key={row.batch_id}>
                                        <td>#{row.batch_id}</td>
                                        <td className="sp-strong">{row.sku}</td>
                                        <td>{row.name || "—"}</td>
                                        <td>{fmtQty(row.qty_on_hand)}</td>
                                        <td className="sp-mini">{fmtDate(row.expiration_date)}</td>
                                        <td><span className={Number(row.days_left) <= 30 ? "sp-pill warn" : "sp-pill"}>{row.days_left}</span></td>
                                    </tr>
                                ))}
                                {expiring.length === 0 && <tr><td className="sp-mini" colSpan={6}>Истекающих партий нет</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------
   Вкладка «Настройки» — флаги контура (business_settings, DB-first)
   ------------------------------------------------------------------ */
const FLAGS = [
    { key: "handover_writeoff_enabled", column: "own_warehouse_handover_writeoff_enabled", label: "Списывать по подтверждению НП", hint: "Только по подтверждённому составу посылки и коду из явного списка. Включать после миграции, режима наблюдения и сверки кодов с официальной документацией НП." },
    { key: "returns_enabled", column: "own_warehouse_returns_enabled", label: "Документы возвратов", hint: "Поиск по исходной ТТН, черновики и проведение после приёмки годных товаров" },
    { key: "enabled", column: "own_warehouse_enabled", label: "Контур включён", hint: "Главный выключатель. Выключен — API контура отвечает 503, офферы и заказы склада не обрабатываются." },
    { key: "offers_enabled", column: "own_warehouse_offers_enabled", label: "Публиковать офферы складов", hint: "Остатки складов попадают в business_store_offers и дальше в Tabletki/маркетплейсы." },
    { key: "publish_dry_run", column: "own_warehouse_publish_dry_run", label: "Публикация в режиме dry-run", hint: "Офферы собираются и логируются, но не записываются. Снимите, когда проверили dry-run на вкладке «Цены»." },
    { key: "orders_enabled", column: "own_warehouse_orders_enabled", label: "Участие в заказах", hint: "Отправитель склада участвует в выборе поставщика и резервирует остаток при приёме заказа." },
    { key: "prefer_in_selection", column: "own_warehouse_prefer_in_selection", label: "Предпочитать склад при выборе", hint: "Если остаток есть — строка заказа уходит на склад, а не поставщику (Tabletki и маркетплейсы)." },
    { key: "dry_run", column: "own_warehouse_dry_run", label: "Заказы в режиме dry-run", hint: "Резерв и ТТН не создаются, решения только логируются. Снимите после проверки на живом заказе." },
    { key: "status_sync_enabled", column: "own_warehouse_status_sync_enabled", label: "Реакция на статусы SalesDrive", hint: "Статусы SalesDrive больше не подтверждают списание. Они используются только для сверки и снятия безопасного резерва." },
    { key: "write_off_after_release", column: "own_warehouse_write_off_after_release", label: "Списание после снятия резерва", hint: "Что делать, если статус «В дороге НП» пришёл после того, как резерв уже сняли: issue — завести инцидент и уведомить в Telegram, write_off_unreserved — списать остаток без резерва.", type: "text" },
];

const policyToText = (policy) => ({
    write_off: (policy?.write_off || []).join(", "),
    release: (policy?.release || []).join(", "),
    noop: (policy?.noop || []).join(", "),
    return: (policy?.return || []).join(", "),
});

const SettingsTab = () => {
    const [settings, setSettings] = useState(null);
    const [draft, setDraft] = useState(null);
    const [policy, setPolicy] = useState(policyToText(null));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const load = useCallback(async () => {
        setError("");
        try {
            const data = await getOwnWarehouseSettings();
            const next = data?.settings || {};
            setSettings(data);
            setDraft({
                ...next,
                salesdrive_target_status_id: next.salesdrive_target_status_id ?? "",
                insufficient_stock_status_id: next.insufficient_stock_status_id ?? "",
                reservation_ttl_hours: next.reservation_ttl_hours ?? "",
                write_off_after_release: next.write_off_after_release ?? "issue",
            });
            setPolicy(policyToText(next.status_policy));
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить настройки"));
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!draft) return;
        setBusy(true);
        setError("");
        setMessage("");
        try {
            const payload = {};
            FLAGS.forEach((flag) => {
                payload[flag.column] = flag.type === "text" ? (draft[flag.key] || null) : Boolean(draft[flag.key]);
            });
            payload.own_warehouse_status_policy = {
                write_off: splitIds(policy.write_off),
                release: splitIds(policy.release),
                noop: splitIds(policy.noop),
                return: splitIds(policy.return),
            };
            payload.own_warehouse_salesdrive_target_status_id = toNumberOrNull(draft.salesdrive_target_status_id);
            payload.own_warehouse_insufficient_stock_status_id = toNumberOrNull(draft.insufficient_stock_status_id);
            payload.own_warehouse_reservation_ttl_hours = toNumberOrNull(draft.reservation_ttl_hours);
            await updateOwnWarehouseSettings(payload);
            setMessage("Настройки сохранены — применяются без рестарта (кэш до 60 секунд)");
            await load();
        } catch (err) {
            setError(apiError(err, "Не удалось сохранить настройки"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="sp-section">
            <div className="sp-card sp-panel">
                <h3>Флаги контура «Склад»</h3>
                <p className="lead">Хранятся в business_settings (колонки own_warehouse_*). Значение из БД имеет приоритет над env; пустое поле — берётся env/дефолт. Применяются без рестарта сервисов, кэш загрузчика — 60 секунд.</p>
                <Msg error={error} message={message} />
                {!draft ? <p className="sp-hint">Загрузка…</p> : (
                    <>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">⚑</span><h4>Переключатели</h4><span className="hint">источник: {settings?.settings?.source || "—"}</span></div>
                            <div className="ow-settings-grid">
                                {FLAGS.map((flag) => (
                                    <div key={flag.key} className="ow-flag">
                                        {flag.type === "text" ? (
                                            <SpSelect label={flag.label} value={draft[flag.key] || ""} onChange={(value) => setDraft({ ...draft, [flag.key]: value })} options={[{ value: "issue", label: "issue — инцидент" }, { value: "write_off_unreserved", label: "write_off_unreserved — списать без резерва" }]} placeholder={null} />
                                        ) : (
                                            <SpCheck label={flag.label} checked={Boolean(draft[flag.key])} onChange={(value) => setDraft({ ...draft, [flag.key]: value })} />
                                        )}
                                        <div className="d">{flag.hint} <span className="sp-mini">({flag.column})</span></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">↔</span><h4>Политика статусов SalesDrive</h4><span className="hint">id статусов через запятую</span></div>
                            <div className="sp-grid2">
                                <SpInput label="Списать со склада (по умолчанию 11, 12 — «В дороге НП»)" value={policy.write_off} onChange={(value) => setPolicy({ ...policy, write_off: value })} />
                                <SpInput label="Снять резерв (по умолчанию 6, 17, 8 — отмена/отказ)" value={policy.release} onChange={(value) => setPolicy({ ...policy, release: value })} />
                                <SpInput label="Ничего не делать (по умолчанию 14)" value={policy.noop} onChange={(value) => setPolicy({ ...policy, noop: value })} />
                                <SpInput label="Возврат — ручной (по умолчанию 7)" value={policy.return} onChange={(value) => setPolicy({ ...policy, return: value })} />
                            </div>
                        </div>
                        <div className="sp-block">
                            <div className="sp-bh"><span className="ic">#</span><h4>Статусы и сроки</h4></div>
                            <div className="sp-grid3">
                                <SpInput label="Статус карточки после ТТН (по умолчанию 21 «На отправку»)" type="number" value={draft.salesdrive_target_status_id} onChange={(value) => setDraft({ ...draft, salesdrive_target_status_id: value })} />
                                <SpInput label="Статус при нехватке остатка (пусто — карточка остаётся в 1)" type="number" value={draft.insufficient_stock_status_id} onChange={(value) => setDraft({ ...draft, insufficient_stock_status_id: value })} />
                                <SpInput label="TTL резерва, часов (по умолчанию 6)" type="number" value={draft.reservation_ttl_hours} onChange={(value) => setDraft({ ...draft, reservation_ttl_hours: value })} />
                            </div>
                        </div>
                        <div className="sp-save-bar">
                            <span className="note">Порядок включения: контур → dry-run публикации → офферы → заказы в dry-run → заказы → реакция на статусы.</span>
                            <button type="button" className="sp-btn sp-btn-ghost" onClick={load} disabled={busy}>Отменить изменения</button>
                            <button type="button" className="sp-btn sp-btn-primary" onClick={save} disabled={busy}>Сохранить</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------
   Страница: шапка с выбором склада (?wh=), таббар, маршруты
   ------------------------------------------------------------------ */
/* ------------------------------------------------------------------
   Инструкция для вкладки: кружок «?» в правом углу строки вкладок
   ------------------------------------------------------------------ */
const HelpButton = ({ tabKey }) => {
    const [open, setOpen] = useState(false);
    const help = OWN_WAREHOUSE_HELP[tabKey];
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);
    if (!help) return null;
    return (
        <>
            <button
                type="button"
                className="ow-help-btn"
                onClick={() => setOpen(true)}
                aria-label={`Инструкция: ${help.title}`}
                title="Как пользоваться этой страницей"
            >
                ?
            </button>
            {open && (
                <div className="ow-help-backdrop" onClick={() => setOpen(false)} role="presentation">
                    <div className="ow-help-dialog" role="dialog" aria-modal="true" aria-labelledby="ow-help-title" onClick={(event) => event.stopPropagation()}>
                        <div className="ow-help-head">
                            <h3 id="ow-help-title">Инструкция · {help.title}</h3>
                            <button type="button" className="ow-help-close" onClick={() => setOpen(false)} aria-label="Закрыть">×</button>
                        </div>
                        <div className="ow-help-body">
                            <p className="ow-help-intro">{help.intro}</p>
                            {help.sections.map((section) => (
                                <section key={section.heading} className="ow-help-section">
                                    <h4>{section.heading}</h4>
                                    <dl>
                                        {section.items.map(([term, text]) => (
                                            <div key={term} className="ow-help-item">
                                                <dt>{term}</dt>
                                                <dd>{text}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </section>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const OwnWarehousePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [contourEnabled, setContourEnabled] = useState(null);
    const [enabling, setEnabling] = useState(false);

    const reload = useCallback(async () => {
        setError("");
        try {
            const rows = await listOwnWarehouses();
            setWarehouses(Array.isArray(rows) ? rows : (rows?.items || []));
        } catch (err) {
            setError(apiError(err, "Не удалось загрузить склады"));
        } finally {
            setLoading(false);
        }
        try {
            const data = await getOwnWarehouseSettings();
            setContourEnabled(Boolean(data?.settings?.enabled));
        } catch (err) {
            setContourEnabled(null);
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const enableContour = async () => {
        setEnabling(true);
        setError("");
        try {
            const data = await updateOwnWarehouseSettings({ own_warehouse_enabled: true });
            setContourEnabled(Boolean(data?.settings?.enabled));
        } catch (err) {
            setError(apiError(err, "Не удалось включить контур"));
        } finally {
            setEnabling(false);
        }
    };

    const whParam = searchParams.get("wh") || "";
    const selected = useMemo(
        () => warehouses.find((item) => String(item.id) === String(whParam)) || null,
        [warehouses, whParam],
    );
    useEffect(() => {
        if (!loading && !selected && warehouses.length) {
            const next = new URLSearchParams(searchParams);
            next.set("wh", String(warehouses[0].id));
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, selected, warehouses]);

    const activeTab = location.pathname.split("/")[2] || "overview";
    const goTab = (key) => navigate({ pathname: `/warehouse/${key}`, search: location.search });
    const selectWarehouse = (id) => {
        const next = new URLSearchParams(searchParams);
        next.set("wh", String(id));
        setSearchParams(next);
    };
    const repriceSelected = (skus) => navigate({ pathname: "/warehouse/pricing", search: location.search }, { state: { skus } });
    const createWriteoff = (lines) => navigate({ pathname: "/warehouse/writeoffs", search: location.search }, { state: { writeoffLines: lines } });
    const pricingSkus = useMemo(() => (Array.isArray(location.state?.skus) ? location.state.skus : null), [location.state]);
    const redirect = <Navigate to={{ pathname: "/warehouse/overview", search: location.search }} replace />;

    return (
        <main className={`suppliers-page own-warehouse-page${activeTab === "invoices" ? " is-invoices" : ""}`}>
            <div className="ow-main">
                <div className="sp-card sp-toolbar">
                    <div className="sp-toolbar-head">
                        <div>
                            <h2>Склад</h2>
                            <p>Собственные склады: партии FIFO, резерв под заказ, списание при отправке, базовая цена и переоценка. Склад = отправитель = юрлицо.</p>
                        </div>
                        <div className="sp-toolbar-actions">
                            <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={reload}>Обновить</button>
                        </div>
                    </div>
                    <div className="ow-head">
                        <div className="ow-pick">
                            <SpSelect
                                label="Текущий склад"
                                value={selected ? String(selected.id) : ""}
                                onChange={(value) => value && selectWarehouse(value)}
                                options={warehouses.map((item) => ({ value: String(item.id), label: `${item.code} · ${item.name}${item.business_organization_name ? ` · ${item.business_organization_name}` : ""}` }))}
                                placeholder={warehouses.length ? "Выберите склад" : "Складов пока нет"}
                            />
                        </div>
                        {selected && (
                            <div className="sp-stats">
                                <span className={selected.stock_enabled ? "sp-stat" : "sp-stat zero"}>остатки {selected.stock_enabled ? "вкл" : "выкл"}</span>
                                <span className={selected.orders_enabled ? "sp-stat" : "sp-stat zero"}>заказы {selected.orders_enabled ? "вкл" : "выкл"}</span>
                                <span className="sp-stat">SKU в наличии <span>{fmtQty(selected.stats?.sku_in_stock)}</span></span>
                                <span className="sp-stat">остаток <span>{fmtQty(selected.stats?.on_hand_qty)}</span></span>
                                <span className={Number(selected.stats?.reserved_qty) > 0 ? "sp-stat warn" : "sp-stat zero"}>резерв <span>{fmtQty(selected.stats?.reserved_qty)}</span></span>
                                <span className="sp-stat">стоимость <span>{fmtNum(selected.stats?.stock_value)} ₴</span></span>
                            </div>
                        )}
                    </div>
                    {contourEnabled === false && (
                        <div className="sp-callout ow-disabled-callout">
                            <div>
                                <b>Контур собственного склада выключен.</b> Склады и накладные можно заводить, но проведение,
                                корректировки, возвраты и переоценка заблокированы. Включение главного выключателя безопасно:
                                публикация офферов, заказы и обработка статусов включаются отдельными флагами на вкладке «Настройки».
                            </div>
                            <button type="button" className="sp-btn sp-btn-primary sp-btn-sm" onClick={enableContour} disabled={enabling}>
                                {enabling ? "Включаю…" : "Включить контур"}
                            </button>
                        </div>
                    )}
                    {error && <div className="sp-msg err">{error}</div>}
                </div>
                <div className="ow-tabbar-row">
                    <div className="sp-tabbar">
                        {TABS.map((tab) => (
                            <button key={tab.key} type="button" className={activeTab === tab.key ? "sp-tab active" : "sp-tab"} onClick={() => goTab(tab.key)}>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <HelpButton key={activeTab} tabKey={activeTab} />
                </div>
                <Routes>
                    <Route index element={redirect} />
                    <Route path="overview" element={<WarehouseOverviewTab warehouses={warehouses} contourEnabled={contourEnabled} onNavigate={(path) => navigate(path)} onSelectWarehouse={selectWarehouse} />} />
                    <Route path="issues" element={<OwnWarehouseFulfillmentIssuesTab warehouse={selected} />} />
                    <Route path="registry" element={<RegistryTab warehouses={warehouses} selected={selected} onSelect={selectWarehouse} reload={reload} />} />
                    <Route path="np-registries" element={<NpRegistriesTab warehouse={selected} reloadWarehouses={reload} />} />
                    <Route path="stock" element={<StockTab warehouse={selected} onReprice={repriceSelected} onCreateWriteoff={createWriteoff} />} />
                    <Route path="movements" element={<MovementsTab warehouse={selected} />} />
                    <Route path="writeoffs" element={<OwnWarehouseWriteoffsTab key={selected?.id} warehouse={selected} initialLines={location.state?.writeoffLines} onChanged={reload} />} />
                    <Route path="returns" element={selected ? <OwnWarehouseReturnsTab key={selected.id} warehouse={selected} onPosted={reload} /> : <EmptyWarehouse />} />
                    <Route
                        path="invoices"
                        element={selected ? (
                            <FulfillmentPage
                                embedded
                                mode="own_warehouse"
                                ownWarehouseId={selected.id}
                                ownWarehouseCode={selected.code}
                                ownWarehouseOrganizationId={selected.business_organization_id}
                            />
                        ) : <EmptyWarehouse />}
                    />
                    <Route path="pricing" element={<PricingTab warehouse={selected} initialSkus={pricingSkus} />} />
                    <Route path="dynamics" element={<DynamicsTab warehouse={selected} />} />
                    <Route path="external-stocks" element={<WarehouseExternalStocksTab />} />
                    <Route path="settings" element={<SettingsTab />} />
                    <Route path="*" element={redirect} />
                </Routes>
            </div>
        </main>
    );
};

export default OwnWarehousePage;
